import { useState, useEffect, useRef, useCallback, useLayoutEffect, useContext, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { parseItemPath, drawingPath, parseViewParam } from "../utils/itemLinks";
import api, { getProcessingStatus } from "../utils/api";
import { SocketContext } from "../contexts/SocketContext";
import { AuthContext } from "../contexts/AuthContext";
import CheckpointSteering from "./CheckpointSteering";
import ProcessingOverlay from "../components/ProcessingOverlay";
import DrawingMinimap from "../components/DrawingMinimap";
import CanvasStackPicker from "../components/CanvasStackPicker";
import {
  Box,
  CircularProgress,
  Alert,
} from "@mui/material";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import FindReplaceIcon from "@mui/icons-material/FindReplace";
import "./DrawingView.css";
import Connection from "../components/Connection";
import { nearestPointOnPolyline } from "../utils/geometry";
import { getVlmState, isStalePending, VLM_COLORS } from "../utils/vlmStatus";
import { findUnlinked, DESCRIBING_TYPES } from "../utils/graphCompleteness";
import { getLabelForType } from "../data/equipmentTypes";
import { undoCalls, lineStyleUndoCalls } from "../utils/contactSheet";
import Element from "../components/Element";
import ConnectionEditDialog from "../components/ConnectionEditDialog";
import DrawingSidePane from "../components/DrawingSidePane";
import ContactSheet from "../components/ContactSheet";
import ConnectionCreationOverlay from "../components/ConnectionCreationOverlay";
import HeaderNotifications from "../components/HeaderNotifications";
import { dropReplacedLinks } from "../utils/replacedLinks";


// Returns a 1- or 2-segment orthogonal path between two element centres.
function computeOrthogonalPath(src, tgt) {
  const dx = Math.abs(tgt.x - src.x);
  const dy = Math.abs(tgt.y - src.y);
  if (dx < 1 && dy < 1) {
    return [{ orientation: 'horizontal', start: [src.x, src.y], end: [tgt.x, tgt.y] }];
  }
  if (dx > dy) {
    return [
      { orientation: 'horizontal', start: [src.x, src.y], end: [tgt.x, src.y] },
      { orientation: 'vertical',   start: [tgt.x, src.y], end: [tgt.x, tgt.y] },
    ];
  }
  return [
    { orientation: 'vertical',   start: [src.x, src.y], end: [src.x, tgt.y] },
    { orientation: 'horizontal', start: [src.x, tgt.y], end: [tgt.x, tgt.y] },
  ];
}

// Normalise a source/target value to a plain string ID. Orphan connections legitimately
// have a null/undefined source or target — must stay undefined (dropped by JSON.stringify)
// rather than becoming the literal string "undefined", which the server would try to
// cast to an ObjectId and crash on.
// How a toast names a text label or pipe gradient that was just linked.
const describerName = (el) => el.name || (el.elementType === 'text_label' ? 'text' : getLabelForType(el.elementType));

// What the Traces toggle hides: pipe and signal lines, and the junctions on
// them, as a pipeline traced them (origin pipeline_claude, or the legacy
// pipeline's cv). Anything else — drawn, split or edited by the user,
// confirming or reversing included, or with no origin recorded — stays.
const TRACE_ORIGINS = new Set(['pipeline_claude', 'cv']);
const isMachineMade = (item) => TRACE_ORIGINS.has(item.metadata?.origin);
const isTraceConnection = (c) => ['flows to', 'controls'].includes(c.relationshipType || 'flows to') && isMachineMade(c);
const isTraceElement = (el) => el.elementType === 'pipe_junction' && isMachineMade(el);

const getElId = (v) => {
  if (v === null || v === undefined) return undefined;
  return (typeof v === 'object' && v?._id) ? String(v._id) : String(v);
};

const VIEW_PREFS_KEY = 'pandid.drawingView.prefs';
const readViewPrefs = () => {
  try { return JSON.parse(localStorage.getItem(VIEW_PREFS_KEY)) || {}; } catch (e) { return {}; }
};

// Id of a connection end (populated object or bare id); null when unset.
const getElementId = (value) => {
  if (!value) return null;
  if (typeof value === 'object' && value._id) return String(value._id);
  return String(value);
};

const DrawingView = () => {

  const { id, '*': itemPath = '' } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const socket = useContext(SocketContext);
  // Reject/"Hide rejected" are internal annotation tooling, not customer-facing
  // — see the matching gate in DrawingSidePane.js and server/utils/roles.js.
  const { user } = useContext(AuthContext);
  const isAdmin = user?.role === 'admin';
  const containerRef = useRef(null);
  const currentElementPositionRef = useRef(null); // Add ref to track current element position
  const dragOffsetRef = useRef({ x: 0, y: 0 }); // Add ref for drag offset that persists between renders
  const pendingScrollBBox = useRef(null);
  const lastZoomCenterTarget = useRef(null); // debounces a same-target double-fire — see zoomKeepingViewportCenter
  const lastZoomedSelectionRef = useRef({ elementId: null, connectionId: null });
  const lastZoomedPreviewRef = useRef(null);   // 'kind:id' of the preview last zoomed to
  const isZoomDraggingRef = useRef(false);
  const sidebarRef = useRef(null);

  // State for drawing data
  const [drawing, setDrawing] = useState(null);
  const [elements, setElements] = useState([]);
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // State for processing
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStatus, setProcessingStatus] = useState(null);
  const [processingMessage, setProcessingMessage] = useState('');
  const processingIntervalRef = useRef(null);

  // Header-bar notification stream — replaces the old floating toasts/snackbars.
  const [notifications, setNotifications] = useState([]);
  const notificationIdRef = useRef(0);

  const notify = useCallback((text, opts = {}) => {
    const id = ++notificationIdRef.current;
    setNotifications((q) => [...q, { id, text, tone: opts.tone || 'success', undo: opts.undo }]);
  }, []);

  const dismissNotification = useCallback((id) => {
    setNotifications((q) => q.filter((n) => n.id !== id));
  }, []);

  const undoNotification = useCallback((id) => {
    setNotifications((q) => {
      const item = q.find((n) => n.id === id);
      if (item?.undo) item.undo();
      return q.filter((n) => n.id !== id);
    });
  }, []);

  // Checkpoint steering
  const [showCheckpoint, setShowCheckpoint] = useState(false);
  // Suggested/accepted-but-unsaved text links (name, descriptions) from the
  // side pane, drawn only: [{ labelId, elementId, accepted }] — see DrawingSidePane.
  const [linkPreviews, setLinkPreviews] = useState([]);

  // Add global references for the drag handler
  useEffect(() => {
    // Make elements and functions available globally for SelectionOverlay to access
    window.currentElements = elements;
    window.setElementsFunction = setElements;
    window.setConnectionsFunction = setConnections;

    // Cleanup function
    return () => {
      delete window.currentElements;
      delete window.setElementsFunction;
      delete window.setConnectionsFunction;
    };
  }, [elements, connections]); // Re-run when elements change


  // State for zoom and window resize
  const [scale, setScale] = useState(1.0);
  // The <img> is rendered at `containerWidth * scale` (see the main image
  // below), so scale===1 already exactly fills the container width — going
  // below that can only ever add empty margin around the drawing, never show
  // more of it. MAX_SCALE is just the existing upper bound, named for symmetry.
  const MIN_SCALE = 1;
  const MAX_SCALE = 8;

  // State for image blob
  const [imageBlobUrl, setImageBlobUrl] = useState(null);
  const [imageLoading, setImageLoading] = useState(false);
  const [naturalImageSize, setNaturalImageSize] = useState(null); // fallback when metadata.image_size missing

  const imageSize = drawing?.metadata?.image_size || naturalImageSize; // [width, height]

  // State for element editing
  const [selectedElement, setSelectedElement] = useState(null);
  const [selectedConnection, setSelectedConnection] = useState(null);
  // A list item being looked at without opening it ({ kind: 'element' |
  // 'connection', id }): highlighted and zoomed to, side pane stays on the list.
  const [previewItem, setPreviewItem] = useState(null);
  const [editConnectionDialogOpen, setEditConnectionDialogOpen] =
    useState(false);
  const [editConnectionData, setEditConnectionData] = useState({
    name: "",
    description: "",
    relationshipType: "",
    source: "",
    target: "",
  });

  const [isCreatingConnection, setIsCreatingConnection] = useState(false);
  const [isCreatingElement, setIsCreatingElement] = useState(false);
  const [elementDrawStart, setElementDrawStart] = useState(null); // image coords {x, y} of first corner click
  const [elementMousePos, setElementMousePos] = useState(null);   // image coords {x, y} for preview

  // State for sidebar
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // View toggles are remembered in this browser (a per-viewer convenience, not
  // drawing data): working with everything visible is the normal way to
  // annotate, so Show all starts on until the viewer turns it off.
  const [showAllObjects, setShowAllObjects] = useState(() => readViewPrefs().showAll ?? true);
  //const [showPipes, setShowPipes] = useState(false);

  // Hide rejected (soft-deleted) elements/connections — off by default so pass 1
  // of ground-truth annotation (reject wrong detections) can still see them to
  // reject; toggled on for pass 2's decluttered canvas. See
  // PIPELINE_IMPROVEMENT_PLAN.md §0.2 item 7 / §0.3 step 3.
  // Remembered too, but only honoured for admins — the toggle is admin-only, so
  // nobody else could switch it back off.
  const [hideRejectedPref, setHideRejected] = useState(() => readViewPrefs().hideRejected ?? false);
  const hideRejected = isAdmin && hideRejectedPref;
  // Traces (admin): show / hide pipe and signal lines and their junctions —
  // decluttering text and symbol work while traced lines are still straight
  // point-to-point placeholders. A selected one still shows.
  const [showTracesPref, setShowTraces] = useState(() => readViewPrefs().showTraces ?? true);
  // Ground-truth drawings (EVAL- / TRAINING-) have no traces: their pipes are
  // drawn by hand and the eval re-runs tracing itself, so neither "Trace lines"
  // nor the Traces toggle is offered there.
  const isGroundTruthDrawing = /^(EVAL|TRAINING)-/i.test(drawing?.name || '');
  const hideTraces = isAdmin && !isGroundTruthDrawing && !showTracesPref;
  useEffect(() => {
    try {
      localStorage.setItem(VIEW_PREFS_KEY, JSON.stringify({ showAll: showAllObjects, hideRejected: hideRejectedPref, showTraces: showTracesPref }));
    } catch (e) { /* storage unavailable — the toggles just won't be remembered */ }
  }, [showAllObjects, hideRejectedPref, showTracesPref]);
  // Last-mile clean-up (everyone): highlight what isn't wired into the graph yet
  // — see utils/graphCompleteness.js for the rules.
  const [showUnlinked, setShowUnlinked] = useState(false);

  // State for maximized view
  const [isMaximized, setIsMaximized] = useState(false);

  // State for element dragging
  const [draggedElement, setDraggedElement] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  // State for container width (for responsive scaling)
  const [containerWidth, setContainerWidth] = useState(0);

  // Live scroll/viewport geometry for the minimap's "you are here" rectangle
  // (DrawingMinimap.js) — pan is native scroll (see scrollToBBox below), with
  // no existing listener anywhere in this file, so this is new. rAF-throttled
  // in the effect that sets it, since native `scroll` fires very frequently.
  const [viewportRect, setViewportRect] = useState(null); // { scrollLeft, scrollTop, clientWidth, clientHeight, scrollWidth, scrollHeight }

  // Rerouting an existing connection: which end is being redrawn, and where the anchor (fixed) end is.
  const [reroutingConnection, setReroutingConnection] = useState(null); // { connectionId, anchoredEnd: 'source'|'target', anchorPoint: {x, y} }

  // Canvas pick mode — set when DrawingSidePane asks user to pick an endpoint on the canvas
  const [canvasPickMode, setCanvasPickMode] = useState(null); // { connectionId, which, onDone }
  // Pending endpoint — set when "Add new element on drawing" is triggered from the picker
  const [pendingEndpoint, setPendingEndpoint] = useState(null); // { connectionId, which }
  const [hoverElementId, setHoverElementId] = useState(null);

  // Click-stack picker — when a click's mousedown lands on more than one
  // element/connection at once (e.g. a text_label tag inside an instrument
  // bubble), offer a list instead of silently only ever reaching whichever
  // one is topmost. { hits: [{type, item}], anchorPosition: {top, left} } | null
  const [stackPicker, setStackPicker] = useState(null);
  // Set alongside stackPicker so the *next* click-capture handler (the
  // native `click` event that follows this mousedown on mouseup) also gets
  // suppressed — stopping propagation on mousedown doesn't stop the
  // separate, later click event a Connection's own onClick relies on.
  const suppressNextClickRef = useRef(false);

  // Zoom tool state
  const [isZoomMode, setIsZoomMode] = useState(false);

  // Rapid annotate (admin), two tools. Symbol: click a symbol → a box of the
  // current size is placed and classified by Claude in the background; drag
  // places a custom box and makes its size the current one. Text: drag a box
  // around text → a text_label is placed and read by OCR in the background.
  // One pass: keep placing, review orange (answered) items through the normal
  // confirm flow whenever.
  const [rapidTool, setRapidTool] = useState(null);       // 'symbol' | 'text' | null (off)
  const isRapidMode = rapidTool !== null;
  const [rapidBoxSize, setRapidBoxSize] = useState(null); // {width, height} image px, remembered per drawing
  const [rapidDrag, setRapidDrag] = useState(null);       // {start: {x, y}, current: {x, y}} image coords while the mouse is down
  const [rapidMouse, setRapidMouse] = useState(null);     // image coords of the cursor, for the ghost box
  const rapidUndoRef = useRef([]);                        // ids placed this session, newest last (Backspace pops)
  const staleRequeuedRef = useRef(null);                  // drawing id whose stale pendings were already re-queued
  const [zoomSelectionStart, setZoomSelectionStart] = useState(null); // image coords {x, y}
  const [zoomSelectionCurrent, setZoomSelectionCurrent] = useState(null); // image coords {x, y}

  // Toggle sidebar function
  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const refreshElementsAndConnections = useCallback(async () => {
    try {
      // Get updated elements for this drawing
      const elementsRes = await api.get(`/api/drawings/${id}/elements`);
      setElements(elementsRes.data);

      const connectionsRes = await api.get(`/api/drawings/${id}/connections`);
      setConnections(connectionsRes.data);
    } catch (err) {
      console.error("Error refreshing elements and connections:", err);
    }
    try {
      // the drawing's metadata (e.g. the Claude pipeline's stage) changes with processing too
      const drawingRes = await api.get(`/api/drawings/${id}`);
      setDrawing(prev => ({ ...prev, ...drawingRes.data }));
    } catch (err) {
      console.error("Error refreshing drawing:", err);
    }
  }, [id]); // id is the only dependency

  // First, memoize the polling function with useCallback
  const startPollingProcessingStatus = useCallback((jobId) => {
    // Clear any existing polling interval
    if (processingIntervalRef.current) {
      clearInterval(processingIntervalRef.current);
    }

    // Set up polling interval (every 5 seconds)
    processingIntervalRef.current = setInterval(async () => {
      try {
        const statusRes = await getProcessingStatus(id);

        // Update processing status
        setProcessingStatus(statusRes.data.drawingStatus);
        setProcessingMessage(statusRes.data.message || '');

        const finalStatuses = ['ready', 'error', 'completed', 'failed', 'checkpoint'];
        if (finalStatuses.includes(statusRes.data.drawingStatus)) {
          clearInterval(processingIntervalRef.current);
          processingIntervalRef.current = null;
          setIsProcessing(false);
          setDrawing(prev => ({
            ...prev,
            status: statusRes.data.drawingStatus,
            processingMessage: statusRes.data.message || ''
          }));

          if (statusRes.data.drawingStatus === 'checkpoint') {
            setShowCheckpoint(true);
          } else if (statusRes.data.drawingStatus === 'ready' || statusRes.data.drawingStatus === 'completed') {
            notify('Processing complete! Refreshing elements...');
            refreshElementsAndConnections();
          } else {
            notify(`Processing failed: ${statusRes.data.message || 'Unknown error'}`, { tone: 'error' });
          }
        }
      } catch (err) {
        console.error('Error checking processing status:', err);
      }
    }, 5000);
  }, [id, refreshElementsAndConnections, notify]); // Include dependencies for this function


  // Load drawing data 
  useEffect(() => {
    const fetchDrawing = async () => {
      setLoading(true);
      try {
        const drawingRes = await api.get(`/api/drawings/${id}`);
        setDrawing(drawingRes.data);

        // Check if drawing is in processing/checkpoint state
        if (drawingRes.data.status === 'checkpoint') {
          setShowCheckpoint(true);
          setIsProcessing(false);
        } else if (drawingRes.data.status === 'processing' && drawingRes.data.metadata?.jobId) {
          setIsProcessing(true);
          startPollingProcessingStatus(drawingRes.data.metadata.jobId);
        } else {
          setIsProcessing(false);
          setProcessingStatus(drawingRes.data.status);
          setProcessingMessage(drawingRes.data.processingMessage || '');
        }

        // Get elements for this drawing
        const elementsRes = await api.get(`/api/drawings/${id}/elements`);
        setElements(elementsRes.data);

        const connectionsRes = await api.get(`/api/drawings/${id}/connections`);
        setConnections(connectionsRes.data);

        setError(null);
      } catch (err) {
        setError("Failed to load drawing data. Please try again.");
        console.error("Error fetching drawing data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchDrawing();

    // Cleanup function to clear polling interval
    return () => {
      if (processingIntervalRef.current) {
        clearInterval(processingIntervalRef.current);
      }
    };
  }, [id, startPollingProcessingStatus]);

  // Fetch image blob when drawing changes
  useEffect(() => {
    if (!drawing?._id) {
      setImageBlobUrl(null);
      setImageLoading(false);
      return;
    }

    const controller = new AbortController();
    let blobUrl = null;
    setImageLoading(true);

    const fetchImage = async () => {
      try {
        // Read token at call time, not at module load time
        const currentToken = localStorage.getItem('token');
        const res = await fetch(`/api/drawings/${drawing._id}/file`, {
          headers: { Authorization: `Bearer ${currentToken}` },
          signal: controller.signal,
        });
        if (!res.ok) {
          throw new Error(`Failed to fetch image: ${res.status} ${res.statusText}`);
        }
        const contentType = res.headers.get("content-type");
        if (!contentType?.startsWith("image/")) {
          throw new Error("Server returned a non-image file type");
        }
        const blob = await res.blob();
        blobUrl = URL.createObjectURL(blob);
        setImageBlobUrl(blobUrl);
      } catch (err) {
        if (err.name === 'AbortError') return; // unmount cleanup — don't update state
        setImageBlobUrl(null);
        setError("Error loading image. " + (err.message || "Unknown error"));
      } finally {
        if (!controller.signal.aborted) {
          setImageLoading(false);
        }
      }
    };

    fetchImage();

    return () => {
      controller.abort();
      if (blobUrl) URL.revokeObjectURL(blobUrl);
      setImageLoading(false);
    };
  }, [drawing?._id]);

  // Socket listener: watch for checkpoint and completion events
  useEffect(() => {
    if (!socket || !id) return;

    const handleProgress = (data) => {
      if (data.drawingId !== id) return;

      if (data.status === 'checkpoint') {
        setIsProcessing(false);
        setShowCheckpoint(true);
      } else if (data.status === 'completed') {
        setShowCheckpoint(false);
        setIsProcessing(false);
        refreshElementsAndConnections();
        notify('Processing complete! Elements updated.');
      } else if (data.status === 'failed') {
        setShowCheckpoint(false);
        setIsProcessing(false);
        notify(`Processing failed: ${data.message || 'Unknown error'}`, { tone: 'error' });
      } else if (data.status === 'processing') {
        setIsProcessing(true);
        setProcessingMessage(data.message || '');
      }
    };

    socket.on('drawingProgress', handleProgress);
    return () => { socket.off('drawingProgress', handleProgress); };
  }, [socket, id, refreshElementsAndConnections, notify]);

  // Calculate displayScale as a derived value
  const displayScale =
    imageSize && imageSize[0] && containerWidth > 0
      ? containerWidth * scale / imageSize[0]
      : 1;

  function scrollToBBox(bbox, instant = false) {
    const container = containerRef.current;
    const displayScale = containerWidth * scale / imageSize[0];

    const bboxCenterX = bbox.x + bbox.width / 2;
    const bboxCenterY = bbox.y + bbox.height / 2;
    const centerX = bboxCenterX * displayScale;
    const centerY = bboxCenterY * displayScale;

    const scaledImageWidth = imageSize[0] * displayScale;
    const scaledImageHeight = imageSize[1] * displayScale;
    const maxScrollX = Math.max(0, scaledImageWidth - container.offsetWidth);
    const maxScrollY = Math.max(0, scaledImageHeight - container.offsetHeight);

    const scrollX = Math.max(0, Math.min(centerX - container.offsetWidth / 2, maxScrollX));
    const scrollY = Math.max(0, Math.min(centerY - container.offsetHeight / 2, maxScrollY));

    container.scrollTo({
      left: scrollX,
      top: scrollY,
      // Repeated zoom steps (holding +/-, dragging the slider) fire faster
      // than a "smooth" scroll can finish — the next call reads scrollLeft/
      // scrollTop mid-animation, computes its center from that transitional
      // position, and the error compounds every step, dragging the view
      // toward a corner. Instant scrolling removes the animation window so
      // every read is always the true, settled position. Only used for
      // programmatic re-centering (see callers); deliberate "jump to X"
      // navigation still animates.
      behavior: instant ? "auto" : "smooth",
    });
  }

  // The page-pixel box to zoom to for an element or a connection (exactly one
  // of them): an element alone (padded, so it's seen in context) or, with
  // `withNeighbours`, together with every element it's connected to; a
  // connection's ends plus its drawn route (padded when the route is all there
  // is, e.g. an orphan). null when a connection has neither ends nor a route.
  const zoomBoxFor = ({ element = null, connection = null, withNeighbours = false }) => {
    const getId = v => {
      if (!v) return null;
      if (typeof v === "object" && v._id) return String(v._id);
      return String(v);
    };
    let related = [];
    let pad = 0;
    if (element) {
      related.push(element);
      if (withNeighbours) {
        const selectedId = String(element._id);
        const connectedIds = new Set();
        for (const c of connections) {
          const sourceId = getId(c.source);
          const targetId = getId(c.target);
          if (!sourceId || !targetId) continue;
          if (sourceId === selectedId) connectedIds.add(targetId);
          if (targetId === selectedId) connectedIds.add(sourceId);
        }
        elements.forEach(el => { if (connectedIds.has(String(el._id))) related.push(el); });
      } else {
        pad = 1.5 * Math.max(element.boundingBox.width, element.boundingBox.height, 20);
      }
    } else if (connection) {
      // Its source and target elements — whichever still exist (an orphan, or
      // a link whose end was deleted, may have one or none).
      for (const endId of [getId(connection.source), getId(connection.target)]) {
        const end = endId && elements.find(el => String(el._id) === endId);
        if (end) related.push(end);
      }
    }
    const xs = related.flatMap(el => [el.boundingBox.x - pad, el.boundingBox.x + el.boundingBox.width + pad]);
    const ys = related.flatMap(el => [el.boundingBox.y - pad, el.boundingBox.y + el.boundingBox.height + pad]);
    const segs = connection?.metadata?.path_data?.path;
    if (Array.isArray(segs) && segs.length > 0) {
      const routePad = related.length ? 0 : 40;
      for (const sg of segs) {
        xs.push(Math.min(sg.start[0], sg.end[0]) - routePad, Math.max(sg.start[0], sg.end[0]) + routePad);
        ys.push(Math.min(sg.start[1], sg.end[1]) - routePad, Math.max(sg.start[1], sg.end[1]) + routePad);
      }
    }
    if (!xs.length) return null;
    return { x: Math.min(...xs), y: Math.min(...ys), width: Math.max(...xs) - Math.min(...xs), height: Math.max(...ys) - Math.min(...ys) };
  };

  // Zoom and scroll so `bbox` (page pixels) fills 80% of the view.
  const zoomToFit = (bbox) => {
    bbox = { ...bbox };
    // Clamp bbox to image bounds
    bbox.x = Math.max(0, bbox.x);
    bbox.y = Math.max(0, bbox.y);
    bbox.width = Math.min(imageSize[0] - bbox.x, bbox.width);
    bbox.height = Math.min(imageSize[1] - bbox.y, bbox.height);

    // Calculate the scale needed to fit the bbox into 80% of the container.
    // `scale` = image width as a multiple of containerWidth, so we must convert:
    //   displayScale (screen px / image px) = containerWidth * scale / imageSize[0]
    // => scale = displayScale * imageSize[0] / containerWidth
    const targetFraction = 0.8;
    const container = containerRef.current;
    const displayScaleX = (container.offsetWidth * targetFraction) / bbox.width;
    const displayScaleY = (container.offsetHeight * targetFraction) / bbox.height;
    const targetDisplayScale = Math.min(displayScaleX, displayScaleY);
    const newScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, targetDisplayScale * imageSize[0] / containerWidth));

    // If scale needs to change, set it and defer scroll
    if (Math.abs(newScale - scale) > 0.01) {
      pendingScrollBBox.current = { bbox, instant: false };
      setScale(newScale);
    } else {
      // If scale is already correct, just scroll
      scrollToBBox(bbox);
    }
  };

  // zoom to extent of selected element or connection
  useLayoutEffect(() => {
    if (isCreatingConnection || isCreatingElement) return;
    if (!selectedElement && !selectedConnection) {
      // Nothing selected: forget the last zoom, so selecting that same item
      // again (e.g. via the unlinked chip after Escape) zooms to it again.
      lastZoomedSelectionRef.current = { elementId: null, connectionId: null };
      return;
    }
    if (!containerRef.current || !imageSize) return;
    if (!containerWidth) return;

    // Determine current selection IDs
    const currentElementId = selectedElement ? String(selectedElement._id) : null;
    const currentConnectionId = selectedConnection ? String(selectedConnection._id) : null;

    // Only zoom if the selection has changed
    if (
      lastZoomedSelectionRef.current.elementId === currentElementId &&
      lastZoomedSelectionRef.current.connectionId === currentConnectionId
    ) {
      return;
    }

    const bbox = zoomBoxFor({ element: selectedElement, connection: selectedConnection, withNeighbours: true });
    // A connection with neither ends nor a route has nothing to show: stay
    // put rather than zooming out to the whole drawing.
    if (bbox) zoomToFit(bbox);

    // After zooming, update the ref
    lastZoomedSelectionRef.current = {
      elementId: currentElementId,
      connectionId: currentConnectionId,
    };

    // eslint-disable-next-line
  }, [isCreatingConnection, isCreatingElement, selectedElement, selectedConnection, elements, connections, containerRef, imageSize, containerWidth, scale]);

  // The item opened last stays previewed, so going back to the list keeps it
  // highlighted (and in view) instead of jumping somewhere else.
  useEffect(() => {
    const item = selectedElement ? { kind: 'element', id: String(selectedElement._id) }
      : selectedConnection ? { kind: 'connection', id: String(selectedConnection._id) } : null;
    if (!item) return;
    lastZoomedPreviewRef.current = `${item.kind}:${item.id}`;   // the selection already zoomed to it
    setPreviewItem(item);
  }, [selectedElement?._id, selectedConnection?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  // zoom to fit a previewed list item (nothing open)
  useLayoutEffect(() => {
    if (isCreatingConnection || isCreatingElement || selectedElement || selectedConnection) return;
    if (!previewItem || !containerRef.current || !imageSize || !containerWidth) return;
    const key = `${previewItem.kind}:${previewItem.id}`;
    if (lastZoomedPreviewRef.current === key) return;
    const item = (previewItem.kind === 'element' ? elements : connections).find(x => String(x._id) === previewItem.id);
    if (!item) return;
    const bbox = zoomBoxFor(previewItem.kind === 'element' ? { element: item } : { connection: item });
    if (bbox) zoomToFit(bbox);
    lastZoomedPreviewRef.current = key;
    // eslint-disable-next-line
  }, [previewItem, isCreatingConnection, isCreatingElement, selectedElement, selectedConnection, elements, connections, imageSize, containerWidth]);

  // ── Links to an item (utils/itemLinks.js) ─────────────────────────────────
  // The address bar follows the open item, so it is always a link to it; and a
  // link (or the browser's Back / Forward) opens the item it names.
  const selectionRef = useRef({ selectedElement, selectedConnection });
  selectionRef.current = { selectedElement, selectedConnection };
  const pendingLinkRef = useRef(null);   // 'kind:id' a link is opening, until the selection catches up
  // Links wait for the drawing's data and for its image to load: until then the canvas
  // has no size, and a scroll to the item is clamped to the top-left corner.
  const linksReady = !loading && !imageLoading && Boolean(imageBlobUrl) && Boolean(naturalImageSize);

  // URL -> selection: on load, and whenever the address changes (a link, Back).
  useEffect(() => {
    if (!linksReady) return;
    const want = parseItemPath(itemPath);
    const { selectedElement: se, selectedConnection: sc } = selectionRef.current;
    const haveKey = se ? `element:${se._id}` : sc ? `connection:${sc._id}` : null;
    if (!want) {
      if (itemPath) navigate(drawingPath(id) + location.search, { replace: true });   // not a link we know
      else if (haveKey) { setSelectedElement(null); setSelectedConnection(null); setPreviewItem(null); }   // released, as the pane's Back
      return;
    }
    if (haveKey === `${want.kind}:${want.id}`) return;
    const item = (want.kind === 'element' ? elements : connections).find(x => String(x._id) === want.id);
    if (!item) {
      notify(`That ${want.kind} isn't on this drawing — it may have been deleted.`, { tone: 'error' });
      navigate(drawingPath(id), { replace: true });
      return;
    }
    pendingLinkRef.current = `${want.kind}:${want.id}`;
    if (want.kind === 'element') { setSelectedConnection(null); setSelectedElement(item); }
    else { setSelectedElement(null); setSelectedConnection({ ...item, source: getElementId(item.source), target: getElementId(item.target) }); }
  }, [linksReady, location.key, itemPath]); // eslint-disable-line react-hooks/exhaustive-deps

  // selection -> URL. Opening an item from the list adds a history entry (Back
  // returns to the list); moving item to item (the rapid flow) or closing
  // replaces it, so Back doesn't step through every confirm.
  useEffect(() => {
    if (!linksReady) return;
    const item = selectedElement ? { kind: 'element', id: String(selectedElement._id) }
      : selectedConnection ? { kind: 'connection', id: String(selectedConnection._id) } : null;
    if (pendingLinkRef.current) {
      if (item && `${item.kind}:${item.id}` === pendingLinkRef.current) pendingLinkRef.current = null;
      return;
    }
    const target = drawingPath(id, item);
    if (location.pathname === target) return;
    navigate(target, { replace: !(item && !parseItemPath(itemPath)) });
  }, [linksReady, selectedElement?._id, selectedConnection?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ?view=x,y,w,h: zoom to a region and outline it, until something is opened.
  const [viewMarker, setViewMarker] = useState(null);
  const viewParam = new URLSearchParams(location.search).get('view');
  useLayoutEffect(() => {
    const box = parseViewParam(viewParam);
    if (!linksReady || !box || !containerRef.current || !imageSize || !containerWidth) return;
    if (parseItemPath(itemPath)) return;
    setViewMarker(box);
    zoomToFit({ x: box.x - box.width, y: box.y - box.height, width: box.width * 3, height: box.height * 3 });
  }, [linksReady, viewParam, imageSize, containerWidth]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (selectedElement || selectedConnection) setViewMarker(null); }, [selectedElement, selectedConnection]);

  // After scale changes, if we have a pending scroll, do it now
  useEffect(() => {
    if (pendingScrollBBox.current) {
      const { bbox, instant } = pendingScrollBBox.current;
      scrollToBBox(bbox, instant);
      pendingScrollBBox.current = null;
    }
    // eslint-disable-next-line
  }, [scale, containerWidth, imageSize]);

  // Track live scroll/viewport geometry for the minimap's viewport rectangle
  // (DrawingMinimap.js) — rAF-throttled since native `scroll` fires on every
  // pixel of movement. Also re-reads on resize, since offsetWidth/offsetHeight
  // (and therefore the rectangle's size) change independently of scrolling.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    let rafId = null;
    const readRect = () => {
      rafId = null;
      setViewportRect({
        scrollLeft: container.scrollLeft,
        scrollTop: container.scrollTop,
        clientWidth: container.clientWidth,
        clientHeight: container.clientHeight,
        scrollWidth: container.scrollWidth,
        scrollHeight: container.scrollHeight,
      });
    };
    const onScrollOrResize = () => {
      if (rafId != null) return;
      rafId = requestAnimationFrame(readRect);
    };
    readRect();
    container.addEventListener('scroll', onScrollOrResize, { passive: true });
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      container.removeEventListener('scroll', onScrollOrResize);
      window.removeEventListener('resize', onScrollOrResize);
      if (rafId != null) cancelAnimationFrame(rafId);
    };
    // eslint-disable-next-line
  }, [containerRef, scale, containerWidth, imageSize, isMaximized]);

  // Handle wheel zoom
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Track pinch gestures for touch devices
    let initialDistance = null;
    let initialScale = null;

    // Add event prevention for trackpad pinch events (Safari/macOS)
    const preventDefaultZoom = (e) => {
      // Check for the gesture events that indicate pinching on a trackpad
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
      }
    };

    const handleWheel = (e) => {
      // Only zoom if the CTRL key is pressed (Windows/Linux) or CMD key (Mac)
      // This handles the trackpad pinch gesture which typically sends wheel events with ctrlKey=true
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();

        // Calculate new scale based on wheel delta
        // Use smaller increments for smoother zooming
        const delta = -e.deltaY * 0.01;
        const newScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, scale + delta));

        // Get cursor position in the container
        const rect = container.getBoundingClientRect();
        const offsetX = e.clientX - rect.left;
        const offsetY = e.clientY - rect.top;

        // Calculate current scroll position as a fraction of the content
        const scrollFractionX =
          (container.scrollLeft + offsetX) / container.scrollWidth;
        const scrollFractionY =
          (container.scrollTop + offsetY) / container.scrollHeight;

        // Apply the scale change
        setScale(newScale);

        // After the content has resized, adjust scroll position to zoom toward cursor
        // This needs to happen after the render, so we use a timeout
        setTimeout(() => {
          const newScrollX = scrollFractionX * container.scrollWidth - offsetX;
          const newScrollY = scrollFractionY * container.scrollHeight - offsetY;
          container.scrollTo(newScrollX, newScrollY);
        }, 0);
      }
    };

    // Touch event handlers for pinch zoom
    const handleTouchStart = (e) => {
      if (e.touches.length === 2) {
        e.preventDefault();
        // Calculate initial distance between touch points
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        initialDistance = Math.sqrt(dx * dx + dy * dy);
        initialScale = scale;
      }
    };

    const handleTouchMove = (e) => {
      if (initialDistance !== null && e.touches.length === 2) {
        e.preventDefault();
        // Calculate new distance
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        const distance = Math.sqrt(dx * dx + dy * dy);

        // Calculate center of pinch
        const centerX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
        const centerY = (e.touches[0].clientY + e.touches[1].clientY) / 2;

        // Get cursor position in the container
        const rect = container.getBoundingClientRect();
        const offsetX = centerX - rect.left;
        const offsetY = centerY - rect.top;

        // Calculate current scroll position as a fraction of the content
        const scrollFractionX =
          (container.scrollLeft + offsetX) / container.scrollWidth;
        const scrollFractionY =
          (container.scrollTop + offsetY) / container.scrollHeight;

        // Scale based on distance change
        const scaleFactor = distance / initialDistance;
        const newScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, initialScale * scaleFactor));

        // Apply the scale change
        setScale(newScale);

        // After the content has resized, adjust scroll position to zoom toward center of pinch
        setTimeout(() => {
          const newScrollX = scrollFractionX * container.scrollWidth - offsetX;
          const newScrollY = scrollFractionY * container.scrollHeight - offsetY;
          container.scrollTo(newScrollX, newScrollY);
        }, 0);
      }
    };

    const handleTouchEnd = () => {
      initialDistance = null;
      initialScale = null;
    };

    // Set up a global event handler for wheel events with Ctrl key
    // This prevents Chrome's default zoom behavior on the entire page
    const preventGlobalZoom = (e) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        return false;
      }
    };

    // Add the global event listener to the document
    document.addEventListener('wheel', preventGlobalZoom, { passive: false });

    // Add gesture event listeners specific for Safari/macOS
    container.addEventListener('gesturestart', preventDefaultZoom, { passive: false });
    container.addEventListener('gesturechange', preventDefaultZoom, { passive: false });
    container.addEventListener('gestureend', preventDefaultZoom, { passive: false });

    // Add event listeners to the container for our custom zoom behavior
    container.addEventListener("wheel", handleWheel, { passive: false });
    container.addEventListener("touchstart", handleTouchStart, { passive: false });
    container.addEventListener("touchmove", handleTouchMove, { passive: false });
    container.addEventListener("touchend", handleTouchEnd);

    return () => {
      // Remove all event listeners on cleanup
      document.removeEventListener('wheel', preventGlobalZoom);
      container.removeEventListener('gesturestart', preventDefaultZoom);
      container.removeEventListener('gesturechange', preventDefaultZoom);
      container.removeEventListener('gestureend', preventDefaultZoom);
      container.removeEventListener("wheel", handleWheel);
      container.removeEventListener("touchstart", handleTouchStart);
      container.removeEventListener("touchmove", handleTouchMove);
      container.removeEventListener("touchend", handleTouchEnd);
    };
  }, [scale]);

  // Handle edit button click for opening edit dialog
  // const handleOpenElementEditDialog = (element) => {
  //   // First select the element
  //   setSelectedElement(element);

  //   setEditElementData({
  //     name: element.name || "",
  //     description: element.description || "",
  //     relationshipType: "",
  //     source: "",
  //     target: "",
  //   });

  //   setEditElementDialogOpen(true);
  // };

  // Handle element selection (without opening edit dialog)
  const handleElementSelect = (element) => {
    // Toggle selection - deselect if already selected
    if (selectedElement?._id === element._id) {
      setSelectedElement(null);
      setSelectedConnection(null);
    } else {
      // Clear any selected connection when selecting an element
      setSelectedConnection(null);
      // Just select the element, don't open edit dialog
      setSelectedElement(element);
    }
  };

  // Handle element dialog close
  // const handleElementDialogClose = () => {
  //   setEditElementDialogOpen(false);
  // };

  //Handle connection dialog close
  const handleConnectionDialogClose = () => {
    setEditConnectionDialogOpen(false);
  };

  // Handle element update
  // const handleElementUpdate = async () => {
  //   try {
  //     const updatedElement = {
  //       ...selectedElement,
  //       name: editElementData.name,
  //       description: editElementData.description,
  //       confirmed: selectedElement.confirmed,
  //     };

  //     // Call API to update element
  //     const res = await api.put(
  //       `/api/elements/${selectedElement._id}`,
  //       updatedElement
  //     );

  //     // Update local state with the response data
  //     const updatedElements = elements.map((el) =>
  //       el._id === selectedElement._id ? res.data : el
  //     );
  //     setElements(updatedElements);

  //     setEditElementDialogOpen(false);
  //   } catch (err) {
  //     console.error("Error updating element:", err);
  //   }
  // };

  // Handle connection click for opening the edit dialog
  const handleConnectionClick = (connection) => {
    // This will now only be used from the side panel
    setSelectedConnection(connection);

    // Set up connection edit data
    setEditConnectionData({
      name: connection.name || "",
      description: connection.description || "",
      relationshipType: connection.relationshipType || "flows to",
      source: connection.source || "",
      target: connection.target || "",
    });

    // Open the connection edit dialog
    setEditConnectionDialogOpen(true);
  };

  // Handle connection selection (without opening edit dialog)
  const handleConnectionSelect = (connection) => {
    // Function to normalize the connection data format
    const normalizeConnection = (conn) => {
      // Extract source and target IDs, handling both string and object formats
      const getIdValue = (value) => {
        if (typeof value === "object" && value._id) return value._id;
        return value;
      };

      return {
        ...conn,
        source: getIdValue(conn.source),
        target: getIdValue(conn.target),
      };
    };

    // Toggle selection - deselect if already selected
    if (selectedConnection?._id === connection._id) {
      setSelectedConnection(null);
    } else {
      setSelectedElement(null); // Clear element selection
      // Normalize the connection data before setting it in state
      setSelectedConnection(normalizeConnection(connection));
    }
  };

  // Handle connection update
  const handleConnectionUpdate = async () => {
    try {
      // Extract source and target IDs, handling both string and object formats
      const getIdValue = (value) => {
        if (typeof value === "object" && value._id) return value._id;
        return value;
      };

      const updatedConnection = {
        ...selectedConnection,
        name: editConnectionData.name,
        description: editConnectionData.description,
        confirmed: selectedConnection.confirmed,
        source: getIdValue(editConnectionData.source),
        target: getIdValue(editConnectionData.target),
        relationshipType: editConnectionData.relationshipType,
      };

      // Call API to update connection
      const res = await api.put(
        `/api/connections/${selectedConnection._id}`,
        updatedConnection
      );

      // The response might contain source/target as full objects instead of just IDs
      // Transform the response to ensure consistency with our data model
      const normalizedResponse = {
        ...res.data,
        source: getIdValue(res.data.source),
        target: getIdValue(res.data.target),
      };

      // Update local state with the normalized response data
      const updatedConnections = connections.map((con) =>
        con._id === selectedConnection._id ? normalizedResponse : con
      );
      setConnections(updatedConnections);

      setEditConnectionDialogOpen(false);
    } catch (err) {
      console.error("Error updating connection:", err);
    }
  };

  // Re-centers on whatever's currently in the middle of the viewport after a
  // scale change — same deferred-scroll pattern as zoomToBBox/zoomToPoint
  // above (set pendingScrollBBox, let the effect on `scale` apply it once the
  // resize has happened), just anchored to the current viewport center
  // instead of a clicked point. Without this, +/- and the slider zoom toward
  // the container's top-left corner (scroll position stays put while the
  // content grows/shrinks around it), forcing a re-pan after every click.
  const zoomKeepingViewportCenter = (newScale) => {
    const container = containerRef.current;
    if (!container || !imageSize || !containerWidth) { setScale(newScale); return; }
    // Debounce a same-target double-fire (observed: the minimap's range
    // input can fire onChange twice for a single click, the second call
    // already reflecting the just-applied scale). scrollToBBox's centering
    // scroll animates ("smooth"), so that second call can land mid-animation
    // — reading the container's still-transitioning scroll position — and
    // recompute a wrong target that redirects the in-flight scroll. A short
    // window is enough to catch that same-tick echo without blocking a
    // later, genuinely deliberate re-click at the same scale (e.g. to
    // recenter on wherever the user scrolled to since).
    if (newScale === lastZoomCenterTarget.current) return;
    lastZoomCenterTarget.current = newScale;
    setTimeout(() => {
      if (lastZoomCenterTarget.current === newScale) lastZoomCenterTarget.current = null;
    }, 250);
    const currentDisplayScale = containerWidth * scale / imageSize[0];
    const centerPt = {
      x: (container.scrollLeft + container.offsetWidth / 2) / currentDisplayScale,
      y: (container.scrollTop + container.offsetHeight / 2) / currentDisplayScale,
      width: 0, height: 0,
    };
    if (Math.abs(newScale - scale) > 0.01) {
      pendingScrollBBox.current = { bbox: centerPt, instant: true };
      setScale(newScale);
    } else {
      scrollToBBox(centerPt, true);
    }
  };

  // Mapped logarithmically over [MIN_SCALE, MAX_SCALE] rather than linearly —
  // equal raw-scale steps feel very uneven (100%->200% is a much smaller
  // practical jump than 400%->800%), so a linear slider (or linear +/- step)
  // compresses the range most work happens in into a sliver of the track.
  // Shared by the minimap slider and the +/- buttons below, so both controls
  // feel consistent with each other.
  const sliderTToScale = (t) => MIN_SCALE * Math.pow(MAX_SCALE / MIN_SCALE, Math.max(0, Math.min(1, t)));
  const scaleToSliderT = (s) => Math.log(s / MIN_SCALE) / Math.log(MAX_SCALE / MIN_SCALE);

  // Handle scale change — a fixed step in slider-t space (not raw scale), so
  // each click feels like the same proportional zoom regardless of current
  // level, matching the slider.
  const ZOOM_BUTTON_STEP_T = 0.1;
  const handleZoomIn = () => {
    zoomKeepingViewportCenter(sliderTToScale(scaleToSliderT(scale) + ZOOM_BUTTON_STEP_T));
  };

  const handleZoomOut = () => {
    if (scale > MIN_SCALE) {
      zoomKeepingViewportCenter(sliderTToScale(scaleToSliderT(scale) - ZOOM_BUTTON_STEP_T));
    }
  };

  const zoomToBBox = (bbox) => {
    if (!containerRef.current || !imageSize || !containerWidth) return;
    const container = containerRef.current;
    const dsX = (container.offsetWidth * 0.85) / bbox.width;
    const dsY = (container.offsetHeight * 0.85) / bbox.height;
    const newScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, Math.min(dsX, dsY) * imageSize[0] / containerWidth));
    if (Math.abs(newScale - scale) > 0.01) {
      pendingScrollBBox.current = { bbox, instant: false };
      setScale(newScale);
    } else {
      scrollToBBox(bbox);
    }
  };

  const zoomToPoint = (imageX, imageY, delta) => {
    const newScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, scale + delta));
    const pt = { x: imageX - 10, y: imageY - 10, width: 20, height: 20 };
    if (Math.abs(newScale - scale) > 0.01) {
      pendingScrollBBox.current = { bbox: pt, instant: true };
      setScale(newScale);
    } else {
      scrollToBBox(pt, true);
    }
  };

  const handleMinimapZoomSlider = (t) => {
    zoomKeepingViewportCenter(sliderTToScale(t));
  };

  // Ensures add-element / add-connection / zoom stay mutually exclusive —
  // entering one mode cancels whichever of the others is active.
  const cancelOtherModes = (except) => {
    if (except !== 'element' && isCreatingElement) handleCancelElementCreation();
    if (except !== 'connection' && isCreatingConnection) handleConnectionCreationCancel();
    if (except !== 'zoom' && isZoomMode) setIsZoomMode(false);
    if (except !== 'rapid' && isRapidMode) exitRapidMode();
    if (canvasPickMode) handleExitCanvasPickMode();
  };

  const handleZoomModeToggle = () => {
    if (isZoomMode) {
      setIsZoomMode(false);
    } else {
      cancelOtherModes('zoom');
      setIsZoomMode(true);
    }
    setZoomSelectionStart(null);
    setZoomSelectionCurrent(null);
    isZoomDraggingRef.current = false;
  };

  const handleZoomMouseDown = (e) => {
    if (!isZoomMode) return;
    if (e.button !== 0) return;
    e.preventDefault();
    const rect = containerRef.current.getBoundingClientRect();
    const ds = displayScale;
    const x = (e.clientX - rect.left + containerRef.current.scrollLeft) / ds;
    const y = (e.clientY - rect.top + containerRef.current.scrollTop) / ds;
    isZoomDraggingRef.current = false;
    setZoomSelectionStart({ x, y });
    setZoomSelectionCurrent({ x, y });
  };

  const handleZoomMouseMove = (e) => {
    if (!isZoomMode || !zoomSelectionStart) return;
    const rect = containerRef.current.getBoundingClientRect();
    const ds = displayScale;
    const x = (e.clientX - rect.left + containerRef.current.scrollLeft) / ds;
    const y = (e.clientY - rect.top + containerRef.current.scrollTop) / ds;
    setZoomSelectionCurrent({ x, y });
    const dxScreen = Math.abs(x - zoomSelectionStart.x) * ds;
    const dyScreen = Math.abs(y - zoomSelectionStart.y) * ds;
    if (dxScreen > 5 || dyScreen > 5) isZoomDraggingRef.current = true;
  };

  const handleZoomMouseUp = (e) => {
    if (!isZoomMode) return;
    if (e.button !== 0) return;
    const rect = containerRef.current.getBoundingClientRect();
    const ds = displayScale;
    const x = (e.clientX - rect.left + containerRef.current.scrollLeft) / ds;
    const y = (e.clientY - rect.top + containerRef.current.scrollTop) / ds;

    if (isZoomDraggingRef.current && zoomSelectionStart) {
      const bw = Math.abs(x - zoomSelectionStart.x);
      const bh = Math.abs(y - zoomSelectionStart.y);
      if (bw > 5 / ds && bh > 5 / ds) {
        zoomToBBox({ x: Math.min(zoomSelectionStart.x, x), y: Math.min(zoomSelectionStart.y, y), width: bw, height: bh });
      }
    } else {
      zoomToPoint(x, y, 0.4);
    }

    setZoomSelectionStart(null);
    setZoomSelectionCurrent(null);
    isZoomDraggingRef.current = false;
  };

  // ── Rapid annotate ───────────────────────────────────────────────────────
  const rapidSizeKey = `rapidBoxSize:${id}`;

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(rapidSizeKey));
      setRapidBoxSize(saved && saved.width > 0 && saved.height > 0 ? saved : null);
    } catch (e) {
      setRapidBoxSize(null);
    }
  }, [rapidSizeKey]);

  const rememberRapidBoxSize = (size) => {
    setRapidBoxSize(size);
    try { localStorage.setItem(rapidSizeKey, JSON.stringify(size)); } catch (e) { /* per-viewer convenience only */ }
  };

  const exitRapidMode = () => {
    setRapidTool(null);
    setRapidDrag(null);
    setRapidMouse(null);
  };

  // Clicking the active tool's button exits; clicking the other tool switches.
  const handleRapidToolToggle = (tool) => {
    if (rapidTool === tool) {
      exitRapidMode();
      return;
    }
    if (!isRapidMode) cancelOtherModes('rapid');
    setRapidDrag(null);
    setRapidTool(tool);
  };

  const toImageCoords = (e) => {
    const rect = containerRef.current.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left + containerRef.current.scrollLeft) / displayScale,
      y: (e.clientY - rect.top + containerRef.current.scrollTop) / displayScale,
    };
  };

  // Folds a server copy of an element into local state. The selected element is
  // only replaced when the side pane has no unsaved edits — replacing it re-seeds
  // the edit form.
  const applyElementUpdate = useCallback((updated) => {
    setElements(prev => prev.map(el => (el._id === updated._id ? updated : el)));
    setSelectedElement(prev => (
      prev && prev._id === updated._id && !sidebarRef.current?.isElementFormDirty?.() ? updated : prev
    ));
  }, []);

  // Asks the server for the element's assist: OCR for a text label (Text tool),
  // VLM classification for anything else (Symbol tool).
  const requestAssist = useCallback(async (element) => {
    const task = element.elementType === 'text_label' ? 'ocr' : 'classify';
    const pending = { task, status: 'pending', requested_at: new Date().toISOString() };
    applyElementUpdate({ ...element, metadata: { ...(element.metadata || {}), vlm: pending } });
    try {
      const res = await api.post(`/api/elements/${element._id}/${task === 'ocr' ? 'ocr-read' : 'vlm-classify'}`);
      applyElementUpdate(res.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setElements(prev => prev.filter(el => el._id !== element._id)); // deleted meanwhile
      } else {
        // The server marks failures on the element itself; this is a transport
        // error, so show it locally so a retry is offered.
        const failed = { task, status: 'failed', error: err.response?.data?.msg || err.message, failed_at: new Date().toISOString() };
        applyElementUpdate({ ...element, metadata: { ...(element.metadata || {}), vlm: failed } });
      }
    }
  }, [applyElementUpdate]);

  const placeRapidElement = async (boundingBox) => {
    try {
      const res = await api.post(`/api/elements`, {
        drawing: id,
        elementType: rapidTool === 'text' ? 'text_label' : 'ml_symbol',
        boundingBox,
        confirmed: false,
      });
      const created = res.data;
      setElements(prev => [...prev, created]);
      rapidUndoRef.current.push(created._id);
      requestAssist(created);
    } catch (err) {
      notify(`Could not place symbol: ${err.response?.data?.msg || err.message}`, { tone: 'error' });
    }
  };

  // Existing item of the current tool's kind under a click (symbols for the
  // Symbol tool, text labels for the Text tool; rejected items don't count), so
  // clicking an already-annotated one selects it instead of duplicating it.
  const rapidHitTest = (p) => elements.find(el => (
    (el.elementType === 'text_label') === (rapidTool === 'text') && !el.metadata?.rejected && el.boundingBox &&
    p.x >= el.boundingBox.x && p.x <= el.boundingBox.x + el.boundingBox.width &&
    p.y >= el.boundingBox.y && p.y <= el.boundingBox.y + el.boundingBox.height
  ));

  const handleRapidMouseDown = (e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    const p = toImageCoords(e);
    setRapidDrag({ start: p, current: p });
  };

  const handleRapidMouseMove = (e) => {
    const p = toImageCoords(e);
    setRapidMouse(p);
    if (rapidDrag) setRapidDrag(d => ({ ...d, current: p }));
  };

  const handleRapidMouseUp = (e) => {
    if (e.button !== 0 || !rapidDrag) return;
    const { start } = rapidDrag;
    const end = toImageCoords(e);
    setRapidDrag(null);
    const w = Math.abs(end.x - start.x);
    const h = Math.abs(end.y - start.y);

    if (w * displayScale > 6 && h * displayScale > 6) {
      // Drag: place exactly this box (Symbol tool: its size becomes the click size).
      if (rapidTool === 'symbol') rememberRapidBoxSize({ width: w, height: h });
      placeRapidElement({ x: Math.min(start.x, end.x), y: Math.min(start.y, end.y), width: w, height: h });
      return;
    }

    const hit = rapidHitTest(end);
    if (hit) {
      // Select in place — no auto-zoom while clicking around the canvas.
      lastZoomedSelectionRef.current = { elementId: String(hit._id), connectionId: null };
      setSelectedConnection(null);
      setSelectedElement(hit);
      return;
    }
    if (rapidTool === 'text') {
      notify('Drag a box around the text to read it.');
      return;
    }
    if (!rapidBoxSize) {
      notify('Drag a box around one symbol first — its size becomes the click size.');
      return;
    }
    placeRapidElement({
      x: end.x - rapidBoxSize.width / 2,
      y: end.y - rapidBoxSize.height / 2,
      width: rapidBoxSize.width,
      height: rapidBoxSize.height,
    });
  };

  // Backspace with nothing selected: undo the most recent placement this session.
  // (With a selection, the existing Delete/Backspace handler deletes it as usual.)
  useEffect(() => {
    if (!isRapidMode || selectedElement || selectedConnection) return;
    const onKey = async (e) => {
      if (e.key !== 'Backspace') return;
      const tag = e.target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target.isContentEditable) return;
      const lastId = rapidUndoRef.current.pop();
      if (!lastId) return;
      e.preventDefault();
      try {
        await api.delete(`/api/elements/${lastId}`);
        setElements(prev => prev.filter(el => el._id !== lastId));
        notify('Removed last placed symbol');
      } catch (err) {
        notify(`Could not remove symbol: ${err.response?.data?.msg || err.message}`, { tone: 'error' });
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isRapidMode, selectedElement, selectedConnection, notify]);

  // Results also arrive over the socket, so an open view updates even when the
  // HTTP response was lost (navigated away and back, reconnects).
  useEffect(() => {
    if (!socket || !id) return;
    const onVlm = (updated) => {
      if (String(updated.drawing) === String(id)) applyElementUpdate(updated);
    };
    socket.on('elementVlm', onVlm);
    return () => { socket.off('elementVlm', onVlm); };
  }, [socket, id, applyElementUpdate]);

  // Once per drawing load: re-queue classifications stuck in pending (server
  // restarted mid-request, etc.). The server ignores ones still in flight.
  useEffect(() => {
    if (!isAdmin || loading || !id || staleRequeuedRef.current === id) return;
    staleRequeuedRef.current = id;
    elements.filter(el => isStalePending(el)).forEach(el => requestAssist(el));
  }, [isAdmin, loading, id, elements, requestAssist]);

  const vlmCounts = elements.reduce((acc, el) => {
    const s = getVlmState(el);
    if (s) acc[s] += 1;
    return acc;
  }, { pending: 0, review: 0, failed: 0 });

  const unlinked = useMemo(
    () => (showUnlinked ? findUnlinked(elements, connections) : null),
    [showUnlinked, elements, connections]
  );

  // Cycle through unlinked items in list order, starting after the current
  // selection — or, with nothing selected (Escape, or the item just got linked
  // and left the list), after the item this chip last picked, so it carries on
  // rather than restarting from the top.
  const unlinkedCursorRef = useRef(null); // { id, index } of the chip's last pick
  const handleNextUnlinked = () => {
    const list = unlinked?.ordered || [];
    if (!list.length) return;
    const indexOf = (id) => list.findIndex(({ item }) => String(item._id) === String(id));
    let next = 0;
    const current = list.findIndex(({ type, item }) => (
      type === 'element' ? item._id === selectedElement?._id : item._id === selectedConnection?._id
    ));
    if (current !== -1) next = current + 1;
    else if (unlinkedCursorRef.current) {
      const last = indexOf(unlinkedCursorRef.current.id);
      // still unlinked → the one after it; linked since → whatever moved into its place
      next = last !== -1 ? last + 1 : unlinkedCursorRef.current.index;
    }
    next %= list.length;
    const { type, item } = list[next];
    unlinkedCursorRef.current = { id: String(item._id), index: next };
    if (type === 'element') {
      if (item._id !== selectedElement?._id) handleElementSelect(item);
    } else if (item._id !== selectedConnection?._id) {
      handleConnectionSelect(item);
    }
  };

  // Jump to the next item Claude has answered (orange), in list order.
  const handleNextToReview = () => {
    const next = elements.find(el => getVlmState(el) === 'review' && el._id !== selectedElement?._id)
      || elements.find(el => getVlmState(el) === 'review');
    // Confirm's auto-advance may already have landed on it; re-selecting the
    // current selection would toggle it off.
    if (next && next._id !== selectedElement?._id) handleElementSelect(next);
  };

  const handleZoomContextMenu = (e) => {
    if (!isZoomMode) return;
    e.preventDefault();
    const rect = containerRef.current.getBoundingClientRect();
    const ds = displayScale;
    const x = (e.clientX - rect.left + containerRef.current.scrollLeft) / ds;
    const y = (e.clientY - rect.top + containerRef.current.scrollTop) / ds;
    zoomToPoint(x, y, -0.4);
  };

  // Handle showing/hiding all elements
  // const handleToggleAllObjects = (event) => {
  //   setShowAllObjects(event.target.checked);
  //   if (!event.target.checked) {
  //     // When hiding all elements, clear selection
  //     setSelectedElement(null);
  //     setSelectedConnection(null);
  //   }
  // };

  // Handle toggle confirmed Element status
  const handleToggleConfirmedElement = async (element) => {
    try {
      const updatedElement = {
        ...element,
        confirmed: !element.confirmed,
      };

      // Call API to update element
      const res = await api.put(`/api/elements/${element._id}`, updatedElement);

      // Update local state with the response data
      const updatedElements = elements.map((el) =>
        el._id === element._id ? res.data : el
      );
      setElements(updatedElements);

      // If this was the selected element, update it
      if (selectedElement?._id === element._id) {
        setSelectedElement(res.data);
      }
    } catch (err) {
      console.error("Error toggling confirmed status:", err);
    }
  };

  // Handle toggle confirmed Element status
  const handleToggleConfirmedConnection = async (connection) => {
    try {
      const updatedConnection = {
        ...connection,
        confirmed: !connection.confirmed,
      };

      // Call API to update element
      const res = await api.put(
        `/api/connections/${connection._id}`,
        updatedConnection
      );

      // Update local state with the response data
      const updatedConnections = connections.map((con) =>
        con._id === connection._id ? res.data : con
      );
      setConnections(updatedConnections);

      // If this was the selected element, update it
      if (selectedConnection?._id === connection._id) {
        setSelectedConnection(res.data);
      }
    } catch (err) {
      console.error("Error toggling confirmed status:", err);
    }
  };

  // Handle add element
  // const handleAddElement = async () => {
  //   try {
  //     // Calculate position for the new element - center of the visible PDF
  //     const containerBounds = containerRef.current?.getBoundingClientRect();

  //     // Get scroll position
  //     const scrollLeft = containerRef.current?.scrollLeft || 0;
  //     const scrollTop = containerRef.current?.scrollTop || 0;

  //     // Calculate center of the visible area in PDF coordinates (accounting for scale)
  //     const centerX = containerBounds
  //       ? (containerBounds.width / 2 + scrollLeft) / scale
  //       : 300;
  //     const centerY = containerBounds
  //       ? (containerBounds.height / 2 + scrollTop) / scale
  //       : 300;

  //     // Default size for the new element - these will be stored in unscaled units
  //     const width = 40;
  //     const height = 40;

  //     // Create a new node element with empty name (will use placeholder in UI instead)
  //     const newElement = {
  //       drawing: id,
  //       elementType: "node",
  //       name: "",
  //       description: "",
  //       boundingBox: {
  //         x: centerX - width / 2, // Center the element horizontally
  //         y: centerY - height / 2, // Center the element vertically
  //         width: width,
  //         height: height,
  //       },
  //       confirmed: false,
  //     };

  //     // Call API to create the element
  //     const res = await api.post(`/api/elements`, newElement);

  //     // Add the new element to the local state
  //     const updatedElements = [...elements, res.data];
  //     setElements(updatedElements);

  //     // Select the new element
  //     setSelectedElement(res.data);

  //     // Open the edit dialog immediately with empty fields to allow for immediate typing
  //     setEditElementData({
  //       name: "",
  //       description: "",
  //       relationshipType: "",
  //       source: "",
  //       target: "",
  //     });
  //     setEditElementDialogOpen(true);
  //   } catch (err) {
  //     console.error("Error adding new element:", err);
  //   }
  // };

  // Handle add element button click: enter creation mode
  const handleAddElement = () => {
    // Clicking the (highlighted) active button again is the intuitive way to cancel —
    // matches handleZoomModeToggle's pattern, since Escape isn't discoverable.
    if (isCreatingElement) {
      handleCancelElementCreation();
      return;
    }
    cancelOtherModes('element');
    setIsCreatingElement(true);
    setElementDrawStart(null);
    setElementMousePos(null);
    setSelectedElement(null);
    setSelectedConnection(null);
  };

  // Handle cancel element creation
  const handleCancelElementCreation = () => {
    setIsCreatingElement(false);
    setElementDrawStart(null);
    setElementMousePos(null);
    setPendingEndpoint(null);
  };

  // Called by DrawingSidePane to enter canvas pick mode (user clicks an element on canvas to set as endpoint)
  const handleEnterCanvasPickMode = ({ connectionId, which }, onDone) => {
    setCanvasPickMode({ connectionId, which, onDone });
    setHoverElementId(null);
  };

  const handleExitCanvasPickMode = () => {
    setCanvasPickMode(null);
    setHoverElementId(null);
  };

  // Called when the pane's "Add new element on the drawing" footer button is pressed
  const handleAddElementForEndpoint = ({ connectionId, which }) => {
    setPendingEndpoint({ connectionId, which });
    setIsCreatingElement(true);
    setElementDrawStart(null);
    setElementMousePos(null);
    setSelectedElement(null);
    setSelectedConnection(null);
  };

  // Called when user clicks an element while canvasPickMode is active
  const handleCanvasElementPick = (element) => {
    if (!canvasPickMode) return;
    const { onDone } = canvasPickMode;
    setCanvasPickMode(null);
    setHoverElementId(null);
    if (onDone) onDone(element);
  };

  // Click-stack picker (capture-phase mousedown) — when multiple
  // elements/connections overlap at one point, the browser only ever
  // dispatches to whichever DOM node is topmost, so a text_label tag written
  // inside an instrument bubble can never be reached by clicking. Rather than
  // reimplementing hit geometry (and duplicating the isVisible gating that
  // already decides which elements/connections are even in the DOM),
  // `document.elementsFromPoint` reads the *actual* rendered DOM in real
  // paint order — Element.js/Connection.js tag their interactive hit nodes
  // with `data-hit-type`/`data-hit-id` for exactly this. Runs in the capture
  // phase so it can preempt Element's own onMouseDown (which both selects
  // and starts a potential drag) before it fires.
  const handleStackPickerMouseDownCapture = (e) => {
    if (e.button !== 0) return;
    if (isCreatingConnection || isCreatingElement || isZoomMode || isRapidMode) return; // those modes own hit-testing themselves
    if (stackPicker) return; // a picker is already open; let its own UI handle the click

    const nodes = document.elementsFromPoint(e.clientX, e.clientY);
    const seen = new Set();
    const hits = [];
    for (const node of nodes) {
      const { hitType, hitId } = node.dataset || {};
      if (!hitType || !hitId) continue;
      const key = `${hitType}:${hitId}`;
      if (seen.has(key)) continue;
      seen.add(key);
      const item = hitType === 'element'
        ? elements.find((el) => String(el._id) === hitId)
        : connections.find((c) => String(c._id) === hitId);
      if (item) hits.push({ type: hitType, item });
    }

    if (hits.length <= 1) return; // unambiguous — let normal selection/drag proceed untouched

    // If whatever's genuinely topmost at this point (elementsFromPoint is
    // already paint-order-first) is already the current selection, native
    // dispatch reaches it on its own — Element.js promotes a selected
    // element's zIndex, so picking something from the stack once makes it
    // the real topmost node for every subsequent gesture at that point.
    // Without this, "pick the underlying item, then drag it" was a Doh! —
    // the very next mousedown re-triggered the picker instead of the drag,
    // since ambiguity was judged purely on overlap, never on what's already
    // selected. Matches the intended mental model: once something's
    // selected, that's what you're interacting with; to reach a different
    // item in the same stack, deselect first (click empty canvas) and pick
    // again.
    const topmostHit = hits[0];
    const topmostIsAlreadySelected =
      (topmostHit.type === 'element' && selectedElement?._id === topmostHit.item._id) ||
      (topmostHit.type === 'connection' && selectedConnection?._id === topmostHit.item._id);
    if (topmostIsAlreadySelected) return;

    e.stopPropagation();
    suppressNextClickRef.current = true;
    setStackPicker({ hits, anchorPosition: { top: e.clientY, left: e.clientX } });
  };

  // Companion capture-phase click handler — stopping propagation on the
  // mousedown above doesn't stop the separate `click` event a Connection's
  // own onClick-based selection relies on, so that needs suppressing too.
  const handleStackPickerClickCapture = (e) => {
    if (suppressNextClickRef.current) {
      e.stopPropagation();
      suppressNextClickRef.current = false;
    }
  };

  const handleStackPickerPick = (hit) => {
    setStackPicker(null);
    if (hit.type === 'element') {
      if (canvasPickMode) handleCanvasElementPick(hit.item);
      else handleElementSelect(hit.item);
    } else {
      handleConnectionSelect(hit.item);
    }
  };

  // Handle click on drawing area — two-click rectangle draw
  const handleDrawingAreaClick = async (e) => {
    if (!isCreatingElement || !containerRef.current || !imageSize) return;
    if (!e.currentTarget.contains(e.target)) return;

    const rect = containerRef.current.getBoundingClientRect();
    const ds = imageSize && imageSize[0] && containerWidth > 0 ? containerWidth * scale / imageSize[0] : 1;
    const x = (e.clientX - rect.left + containerRef.current.scrollLeft) / ds;
    const y = (e.clientY - rect.top  + containerRef.current.scrollTop)  / ds;

    if (!elementDrawStart) {
      // First click: record first corner
      setElementDrawStart({ x, y });
    } else {
      // Second click: compute bounding box from two corners (order-independent)
      const bx = Math.min(elementDrawStart.x, x);
      const by = Math.min(elementDrawStart.y, y);
      const bw = Math.max(Math.abs(x - elementDrawStart.x), 10);
      const bh = Math.max(Math.abs(y - elementDrawStart.y), 10);

      const newElement = {
        drawing: id,
        elementType: "node",
        name: "",
        description: "",
        boundingBox: { x: bx, y: by, width: bw, height: bh },
        confirmed: true,
      };

      try {
        const res = await api.post(`/api/elements`, newElement);
        const createdElement = res.data;
        setElements(prev => [...prev, createdElement]);

        if (pendingEndpoint) {
          // Auto-wire the new element as the requested endpoint of the pending connection
          const { connectionId, which } = pendingEndpoint;
          const connToWire = connections.find(c => c._id === connectionId);
          if (connToWire) {
            const getIdVal = (v) => (typeof v === 'object' && v?._id) ? v._id : v;
            const patchRes = await fetch(`/api/connections/${connectionId}`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
              body: JSON.stringify({
                source: getIdVal(connToWire.source),
                target: getIdVal(connToWire.target),
                name: connToWire.name,
                relationshipType: connToWire.relationshipType,
                description: connToWire.description,
                [which]: createdElement._id,
                confirmed: false,
              }),
            });
            if (patchRes.ok) {
              const updatedConn = await patchRes.json();
              setConnections(prev => dropReplacedLinks(prev.map(c => c._id === connectionId ? updatedConn : c), updatedConn));
              setSelectedConnection(updatedConn);
              // Wiring this endpoint onto an existing 'labels' connection can
              // rename its target server-side (applyLabelNameDerivation) — patch
              // the local elements cache too, or it shows the stale name until
              // a full reload (same fix as DrawingSidePane.js's connection
              // handlers, mirrored here since this flow uses raw fetch, not
              // DrawingSidePane's shared applyConnectionNameOverwrite).
              const overwrite = updatedConn?.nameOverwrite;
              if (overwrite && updatedConn.targetType !== 'Connection') {
                const renamedId = getIdVal(updatedConn.target);
                setElements(prev => prev.map(el => el._id === renamedId ? { ...el, name: overwrite.newName } : el));
                setSelectedElement(prev => prev?._id === renamedId ? { ...prev, name: overwrite.newName } : prev);
              }
              notify(`Added ${createdElement.name || 'element'} · linked as ${which}`);
            }
          }
          setPendingEndpoint(null);
        } else {
          setSelectedElement(createdElement);
        }
      } catch (err) {
        console.error("Error adding new element:", err);
      } finally {
        setIsCreatingElement(false);
        setElementDrawStart(null);
        setElementMousePos(null);
      }
    }
  };


  // Delete key removes selected element or connection
  useEffect(() => {
    if (!selectedElement && !selectedConnection) return;
    const handleKeyDown = (e) => {
      if (e.key !== 'Delete' && e.key !== 'Backspace') return;
      const tag = e.target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target.isContentEditable) return;
      if (selectedElement) sidebarRef.current?.deleteSelectedElement(selectedElement);
      else if (selectedConnection) sidebarRef.current?.deleteSelectedConnection(selectedConnection);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedElement, selectedConnection]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Listen for Escape key to cancel creation, canvas pick mode, or zoom mode
  useEffect(() => {
    if (!isCreatingElement && !canvasPickMode && !isZoomMode && !isRapidMode) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        exitRapidMode();
        handleCancelElementCreation();
        handleExitCanvasPickMode();
        setIsZoomMode(false);
        setZoomSelectionStart(null);
        setZoomSelectionCurrent(null);
        isZoomDraggingRef.current = false;
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isCreatingElement, canvasPickMode, isZoomMode, isRapidMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Track mouse position for the element draw preview rectangle
  useEffect(() => {
    if (!isCreatingElement || !containerRef.current) return;
    const container = containerRef.current;
    const onMove = (e) => {
      const rect = container.getBoundingClientRect();
      const ds = imageSize && imageSize[0] && containerWidth > 0 ? containerWidth * scale / imageSize[0] : 1;
      setElementMousePos({
        x: (e.clientX - rect.left + container.scrollLeft) / ds,
        y: (e.clientY - rect.top  + container.scrollTop)  / ds,
      });
    };
    container.addEventListener("mousemove", onMove);
    return () => container.removeEventListener("mousemove", onMove);
  }, [isCreatingElement, imageSize, containerWidth, scale]);

  // Add a new function to update element position in database
  const updateElementPosition = async (element, newBoundingBox) => {
    try {
      // The element is already updated in the local state by the drag handler
      // So we just need to persist to the server without updating local state again

      // Call API to update element with new position
      const res = await api.put(`/api/elements/${element._id}`, {
        boundingBox: newBoundingBox,
      });

      // Don't update local state with the response data, as we've already updated it
      // This prevents potential race conditions where the response could have outdated data

      return res.data;
    } catch (err) {
      console.error("Error updating element position:", err);

      // In case of an error, revert to the previous position
      alert("Error updating element position. Changes may not be saved.");
      return null;
    }
  };

  // Handle add connection/relationship between elements
  const handleAddConnection = () => {
    // Clicking the (highlighted) active button again is the intuitive way to cancel —
    // matches handleZoomModeToggle's pattern, since Escape isn't discoverable. Also
    // covers cancelling an in-progress reroute, which shares isCreatingConnection.
    if (isCreatingConnection) {
      handleConnectionCreationCancel();
      return;
    }
    cancelOtherModes('connection');
    setShowAllObjects(true); // Ensure all elements are visible for selection
    setIsCreatingConnection(true);
    setSelectedElement(null);
    setSelectedConnection(null);
  };

  // Handle completion of connection creation
  // Connection tool started on a text label and ended on a pipe: the text
  // describes that pipe. No path (it's an annotation link, drawn as a dotted
  // line to the pipe) and no split of the pipe.
  // A new describes link from a text replaces that text's names link to the same
  // target (and the name it gave, when the name was just that text) — the
  // server says so in the response; keep the local lists in step. Returns the
  // message to show.
  const applyReplacedTextLinks = (data, textName, linkedMessage) => {
    if (!data?.replacedLinks?.length) return linkedMessage;
    setConnections(prev => dropReplacedLinks(prev, data));
    const overwrite = data.nameOverwrite;
    if (overwrite) {
      const targetId = getElId(data.target);
      if (data.targetType === 'Connection') {
        setConnections(prev => prev.map(c => c._id === targetId ? { ...c, name: overwrite.newName } : c));
      } else {
        setElements(prev => prev.map(el => el._id === targetId ? { ...el, name: overwrite.newName } : el));
      }
    }
    return data.replacedLinks.some(l => ['names', 'labels'].includes(l.relationshipType))
      ? `Moved "${textName || "text"}" from name to description`
      : linkedMessage;
  };

  const handleDescribeConnection = async (textLabel, pipe) => {
    setIsCreatingConnection(false);
    try {
      const res = await api.post(`/api/connections`, {
        drawing: id,
        relationshipType: "describes",
        source: textLabel._id,
        target: pipe._id,
        targetType: "Connection",
        confirmed: true,
      });
      setConnections(prev => [...prev, res.data]);
      setSelectedElement(null);
      setSelectedConnection(res.data);
      notify(applyReplacedTextLinks(res.data, textLabel.name, `Linked "${describerName(textLabel)}" as a description of the pipe`));
    } catch (err) {
      notify("Failed to link text to the pipe.", { tone: 'error' });
      console.error(err);
    }
  };

  const handleConnectionCreationComplete = async (source, target, pathArray) => {
    setIsCreatingConnection(false);

    // Text names or describes things, it doesn't flow: a connection started on
    // a text label defaults to 'describes' — non-destructive (adds a
    // description, never overwrites a name); switch it to 'names' in the side
    // pane if that's what it is. Annotation links carry no drawn route, same as
    // the side pane's describes picker creates.
    if (DESCRIBING_TYPES.has(source.elementType)) {
      try {
        const res = await api.post(`/api/connections`, {
          drawing: id,
          relationshipType: "describes",
          source: source._id,
          target: target._id,
          confirmed: true,
        });
        setConnections(prev => [...prev, res.data]);
        setSelectedElement(null);
        setSelectedConnection(res.data);
        notify(applyReplacedTextLinks(res.data, source.name, `Linked "${describerName(source)}" as a description`));
      } catch (err) {
        notify("Failed to create connection.", { tone: 'error' });
        console.error(err);
      }
      return;
    }

    // Compose connection object
    const newConnection = {
      drawing: id,
      name: "",
      relationshipType: "flows to",
      description: "",
      source: source._id,
      target: target._id,
      confirmed: true,
      metadata: {
        path_data: {
          path: pathArray.map((pt, idx) => {
            // Convert to your backend's expected format if needed
            if (idx === 0) return null;
            return {
              orientation: Math.abs(pt.x - pathArray[idx - 1].x) > Math.abs(pt.y - pathArray[idx - 1].y) ? "horizontal" : "vertical",
              start: [pathArray[idx - 1].x, pathArray[idx - 1].y],
              end: [pt.x, pt.y],
            };
          }).filter(Boolean),
        },
      },
    };

    try {
      const res = await api.post(`/api/connections`, newConnection);
      // Functional update — a non-functional `[...connections, res.data]` here would
      // clobber the upstream/downstream connections that handleSplitConnection just
      // wrote via its own functional update when this connection was completed by
      // clicking a line hotspot (split, then immediately complete the draw onto the
      // new junction) — same-gesture state updates race against this closure's stale
      // `connections` snapshot otherwise.
      setConnections(prev => [...prev, res.data]);
      setSelectedElement(null);
      setSelectedConnection(res.data);
      notify("Connection created!");
    } catch (err) {
      notify("Failed to create connection.", { tone: 'error' });
      console.error(err);
    }
  };

  // Handle cancel
  const handleConnectionCreationCancel = () => {
    setIsCreatingConnection(false);
    setReroutingConnection(null);
  };

  // User clicked an endpoint handle on a selected connection — start rerouting from the other end.
  const handleEndpointHandleMouseDown = (connection, which, e) => {
    e.stopPropagation();
    const anchoredEnd = which === 'source' ? 'target' : 'source';
    const anchorElId = getElId(connection[anchoredEnd]);
    const anchorEl = elements.find(el => String(el._id) === anchorElId);
    let anchorPoint;
    if (anchorEl) {
      anchorPoint = {
        x: anchorEl.boundingBox.x + anchorEl.boundingBox.width / 2,
        y: anchorEl.boundingBox.y + anchorEl.boundingBox.height / 2,
      };
    } else {
      // Fall back to the path's fixed-end coordinate (e.g. orphan connections).
      const path = connection.metadata?.path_data?.path ?? [];
      anchorPoint = anchoredEnd === 'source'
        ? { x: path[0]?.start[0] ?? 0, y: path[0]?.start[1] ?? 0 }
        : { x: path.at(-1)?.end[0] ?? 0, y: path.at(-1)?.end[1] ?? 0 };
    }
    // Position of the handle that was clicked (the end being rerouted), used to seed
    // the drawing path so waypoints are placed relative to this point, not the far anchor.
    const connPath = connection.metadata?.path_data?.path ?? [];
    const handlePoint = which === 'source'
      ? { x: connPath[0]?.start[0] ?? anchorPoint.x, y: connPath[0]?.start[1] ?? anchorPoint.y }
      : { x: connPath.at(-1)?.end[0] ?? anchorPoint.x, y: connPath.at(-1)?.end[1] ?? anchorPoint.y };

    setReroutingConnection({ connectionId: connection._id, anchoredEnd, anchorPoint, handlePoint });
    setIsCreatingConnection(true);
    setSelectedConnection(null);
  };

  // Called by ConnectionCreationOverlay when user finishes drawing a rerouted connection.
  const handleRerouteComplete = async (rerouteAnchor, targetElement, pathPoints) => {
    const conn = connections.find(c => c._id === rerouteAnchor.connectionId);
    if (!conn) return;

    let pathSegments = pathPoints.slice(1).map((pt, idx) => ({
      orientation: Math.abs(pt.x - pathPoints[idx].x) > Math.abs(pt.y - pathPoints[idx].y)
        ? 'horizontal' : 'vertical',
      start: [pathPoints[idx].x, pathPoints[idx].y],
      end: [pt.x, pt.y],
    }));

    // Path was drawn anchor→handlePoint→waypoints→newElement.
    // When rerouting source (anchoredEnd='target'): anchor=target, newElement=new source.
    // path_data must go source→target, so reverse the segments.
    if (rerouteAnchor.anchoredEnd === 'target') {
      pathSegments = pathSegments.reverse().map(seg => ({
        orientation: seg.orientation,
        start: seg.end,
        end: seg.start,
      }));
    }

    // The anchored end stays; the rerouted end changes to targetElement.
    const reroutedEnd = rerouteAnchor.anchoredEnd === 'source' ? 'target' : 'source';
    // 'describes'/'names' edges are annotation pointers, not pipe/signal
    // routing — keep them a direct line rather than picking up the 90° bend
    // ConnectionCreationOverlay.js's drag gesture just computed. Same policy
    // as DrawingSidePane.js's handleSaveConnection (DIRECT_ROUTE_RELATIONSHIP_TYPES
    // there); duplicated here rather than shared since it's a two-item literal.
    const isDirectRouteType = ['describes', 'names'].includes(conn.relationshipType);
    const updatedConn = {
      ...conn,
      source: getElId(conn.source),
      target: getElId(conn.target),
      [reroutedEnd]: targetElement._id,
      metadata: isDirectRouteType
        ? { ...conn.metadata, path_data: undefined }
        : { ...conn.metadata, path_data: { path: pathSegments } },
    };

    try {
      const res = await api.put(`/api/connections/${conn._id}`, updatedConn);
      const normalised = { ...res.data, source: getElId(res.data.source), target: getElId(res.data.target) };
      setConnections(prev => dropReplacedLinks(prev.map(c => c._id === conn._id ? normalised : c), res.data));
      // Rerouting a 'labels' connection's endpoint can rename its target
      // server-side (applyLabelNameDerivation) — patch the local elements
      // cache too, or it shows the stale name until a full reload.
      const overwrite = res.data?.nameOverwrite;
      if (overwrite && res.data.targetType !== 'Connection') {
        const renamedId = getElId(res.data.target);
        setElements(prev => prev.map(el => el._id === renamedId ? { ...el, name: overwrite.newName } : el));
        setSelectedElement(prev => prev?._id === renamedId ? { ...prev, name: overwrite.newName } : prev);
      }
    } catch (err) {
      console.error('Error rerouting connection:', err);
    }

    setReroutingConnection(null);
    setIsCreatingConnection(false);
  };

  // Called by ConnectionCreationOverlay when the user clicks a line hotspot — splits the
  // underlying connection at that point, inserting a new pipe_junction element between the
  // kept "upstream" piece and a new "downstream" piece.
  const handleSplitConnection = async (connection, segmentIndex, point) => {
    try {
      const res = await api.post(`/api/connections/${connection._id}/split`, {
        segmentIndex,
        splitPoint: point,
      });
      const { junction, upstreamConnection, downstreamConnection } = res.data;
      setElements(prev => [...prev, junction]);
      setConnections(prev => [
        ...prev.filter(c => c._id !== upstreamConnection._id),
        { ...upstreamConnection, source: getElId(upstreamConnection.source), target: getElId(upstreamConnection.target) },
        { ...downstreamConnection, source: getElId(downstreamConnection.source), target: getElId(downstreamConnection.target) },
      ]);
      notify("Pipe junction created!");
      return { junction };
    } catch (err) {
      notify("Failed to split connection.", { tone: 'error' });
      console.error(err);
      return null;
    }
  };

  // User grabbed a vertex handle — slide the junction along its single free axis.
  const handleVertexMouseDown = (connection, vertexIndex, e) => {
    e.preventDefault();
    e.stopPropagation();

    const container = containerRef.current;
    const ds = displayScale; // captured at mousedown time — stable for the gesture

    const moveHandler = (moveEvent) => {
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const mouseX = (moveEvent.clientX - rect.left + container.scrollLeft) / ds;
      const mouseY = (moveEvent.clientY - rect.top  + container.scrollTop)  / ds;

      setConnections(prev => prev.map(c => {
        if (c._id !== connection._id) return c;
        const segs = c.metadata.path_data.path.map(s => ({ ...s, start: [...s.start], end: [...s.end] }));
        const seg = segs[vertexIndex];
        // The leading segment's orientation determines the free axis:
        //   horizontal → vertex slides left/right (y locked to seg.start[1])
        //   vertical   → vertex slides up/down    (x locked to seg.start[0])
        const newVx = seg.orientation === 'horizontal' ? mouseX : seg.start[0];
        const newVy = seg.orientation === 'horizontal' ? seg.start[1] : mouseY;

        segs[vertexIndex].end = [newVx, newVy];
        segs[vertexIndex + 1].start = [newVx, newVy];

        // Propagate through the next segment so it stays orthogonal.
        const nextSeg = segs[vertexIndex + 1];
        if (nextSeg.orientation === 'vertical') {
          segs[vertexIndex + 1].end[0] = newVx;
          if (segs[vertexIndex + 2]) segs[vertexIndex + 2].start[0] = newVx;
        } else {
          segs[vertexIndex + 1].end[1] = newVy;
          if (segs[vertexIndex + 2]) segs[vertexIndex + 2].start[1] = newVy;
        }

        return { ...c, metadata: { ...c.metadata, path_data: { path: segs } } };
      }));
    };

    const upHandler = () => {
      document.removeEventListener('mousemove', moveHandler);
      document.removeEventListener('mouseup', upHandler);
      setConnections(prev => {
        const conn = prev.find(c => c._id === connection._id);
        if (conn) {
          api.put(`/api/connections/${conn._id}`, {
            ...conn,
            source: getElId(conn.source),
            target: getElId(conn.target),
          }).catch(err => console.error('Error saving vertex drag:', err));
        }
        return prev;
      });
    };

    document.addEventListener('mousemove', moveHandler);
    document.addEventListener('mouseup', upHandler);
  };


  // Handle element deletion
  const handleDeleteConnection = async () => {
    if (!selectedConnection) return;

    try {
      // Call API to delete the element
      await api.delete(`/api/connections/${selectedConnection._id}`);

      // Remove the element from local state
      const updatedConnections = connections.filter(
        (con) => con._id !== selectedConnection._id
      );

      setConnections(updatedConnections);

      // Clear selection and close dialog
      setSelectedElement(null);
      setSelectedConnection(null);
      setEditConnectionDialogOpen(false);
    } catch (err) {
      console.error("Error deleting element:", err);
    }
  };

  // Handle element mouse down - start of drag operation
  const handleElementMouseDown = (e, element) => {
    // Prevent event propagation
    e.preventDefault();
    e.stopPropagation();

    if (e.button !== 0) return; // Only handle left mouse button

    // In canvas pick mode: clicking an element commits it as the new endpoint
    if (canvasPickMode) {
      handleCanvasElementPick(element);
      return;
    }

    // Select the element when mouse down occurs
    if (selectedElement?._id !== element._id) {
      handleElementSelect(element);
    }

    // Pipe junctions are fixed at the pipe intersection they were split from — the
    // connections meeting there store that exact point in their own path_data, so
    // dragging the junction would desync it from those pipes. Selectable, not movable.
    if (element.elementType === 'pipe_junction') return;

    // Calculate offset from the mouse to the top-left corner of the element
    const rect = e.currentTarget.getBoundingClientRect();
    const offsetX = e.clientX - rect.left;
    const offsetY = e.clientY - rect.top;

    // Set up for potential dragging
    dragOffsetRef.current = {
      x: offsetX / displayScale,
      y: offsetY / displayScale,
    };

    // Track initial mouse position to determine if this is a click or drag
    const initialMousePos = { x: e.clientX, y: e.clientY };
    let hasStartedDragging = false;
    let updatedConnectionsForSave = null;

    // Create handlers bound to this specific interaction
    const mouseMoveHandler = (moveEvent) => {
      // Calculate distance moved to determine if this is a drag or just a click
      const dx = moveEvent.clientX - initialMousePos.x;
      const dy = moveEvent.clientY - initialMousePos.y;
      const distance = Math.sqrt(dx * dx + dy * dy);

      // If moved more than 5 pixels, consider it a drag
      if (distance > 5 && !hasStartedDragging) {
        hasStartedDragging = true;
        setIsDragging(true);
        setDraggedElement(element);

        // Change cursor for the entire document while dragging
        document.body.style.cursor = "grabbing";
      }

      // Only update position if we've started dragging
      if (hasStartedDragging) {
        // Get container's position and scroll
        const container = containerRef.current;
        if (!container) return;

        const containerRect = container.getBoundingClientRect();
        const scrollLeft = container.scrollLeft;
        const scrollTop = container.scrollTop;

        // Calculate new element position in document coordinates
        //x = global x - pdfviewer x + window scroll - original click inside the element offset
        const x =
          (moveEvent.clientX -
            containerRect.left +
            scrollLeft -
            dragOffsetRef.current.x * displayScale) /
          displayScale;
        const y =
          (moveEvent.clientY -
            containerRect.top +
            scrollTop -
            dragOffsetRef.current.y * displayScale) /
          displayScale;

        // Update position directly in the element's boundingBox
        const updatedBoundingBox = {
          ...element.boundingBox,
          x: x,
          y: y,
        };

        // Create new element variables with updated position
        const updatedElement = {
          ...element,
          boundingBox: updatedBoundingBox,
        };

        // Store the most current element in our ref to be accessed in mouseUpHandler
        currentElementPositionRef.current = updatedElement;

        // Update elements array with the new position
        const updatedElements = elements.map((el) =>
          el._id === element._id ? updatedElement : el
        );

        // Update state with new element positions
        setElements(updatedElements);
        setDraggedElement(updatedElement); // Also update the dragged element reference

        // Redraw paths for every non-orphan connection that touches the dragged element.
        const draggedId = String(element._id);
        const newConnections = connections.map(conn => {
          const srcId = getElId(conn.source);
          const tgtId = getElId(conn.target);
          const isSrc = srcId === draggedId;
          const isTgt = tgtId === draggedId;
          if ((!isSrc && !isTgt) || conn.metadata?.is_orphan) return conn;
          const srcEl = isSrc ? updatedElement : updatedElements.find(el => String(el._id) === srcId);
          const tgtEl = isTgt ? updatedElement : updatedElements.find(el => String(el._id) === tgtId);
          if (!srcEl || !tgtEl) return conn;
          const srcCenter = {
            x: srcEl.boundingBox.x + srcEl.boundingBox.width / 2,
            y: srcEl.boundingBox.y + srcEl.boundingBox.height / 2,
          };
          const tgtCenter = {
            x: tgtEl.boundingBox.x + tgtEl.boundingBox.width / 2,
            y: tgtEl.boundingBox.y + tgtEl.boundingBox.height / 2,
          };
          return { ...conn, metadata: { ...conn.metadata, path_data: { path: computeOrthogonalPath(srcCenter, tgtCenter) } } };
        });
        updatedConnectionsForSave = newConnections;
        setConnections(newConnections);
      }
    };

    const mouseUpHandler = (upEvent) => {
      // If we've been dragging, save the final position
      if (hasStartedDragging) {
        // Use the position from our ref which has the most current position
        const elementToUpdate = currentElementPositionRef.current;

        if (elementToUpdate) {
          // Send the current position to the server
          api
            .put(`/api/elements/${elementToUpdate._id}`, {
              boundingBox: elementToUpdate.boundingBox,
            })
            .then((response) => {
              // Verify the response contains the updated position
              if (response.data.boundingBox) {
                // Update the elements array with the confirmed position from the server
                const updatedElements = elements.map((el) =>
                  el._id === elementToUpdate._id ? response.data : el
                );
                setElements(updatedElements);

                // Update selection if this was the selected element
                if (selectedElement?._id === elementToUpdate._id) {
                  setSelectedElement(response.data);
                }
              }
            })
            .catch((err) => {
              console.error("Error saving element position:", err);
              // Show an error indicator to the user
              alert("Error saving element position. Changes may not be saved.");
            });
        }

        // Save updated connection paths for connections that were redrawn during drag.
        if (updatedConnectionsForSave) {
          const draggedId = String(element._id);
          updatedConnectionsForSave
            .filter(c => (getElId(c.source) === draggedId || getElId(c.target) === draggedId) && !c.metadata?.is_orphan)
            .forEach(conn => {
              api.put(`/api/connections/${conn._id}`, {
                ...conn,
                source: getElId(conn.source),
                target: getElId(conn.target),
              }).catch(err => console.error('Error saving connection path:', err));
            });
        }

        // Reset drag state
        setIsDragging(false);
        setDraggedElement(null);

        // Reset cursor
        document.body.style.cursor = "";
      }

      // Clean up event listeners
      document.removeEventListener("mousemove", mouseMoveHandler);
      document.removeEventListener("mouseup", mouseUpHandler);
    };

    // Add event listeners to the document, not just the element
    document.addEventListener("mousemove", mouseMoveHandler);
    document.addEventListener("mouseup", mouseUpHandler);
  };

  // Handle corner resize handle mousedown — resizes the selected element
  const handleCornerMouseDown = (e, element, corner) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.button !== 0) return;

    // The corner opposite to the one being dragged stays fixed
    const { x, y, width, height } = element.boundingBox;
    const fixed = {
      x: corner.includes("l") ? x + width : x,
      y: corner.includes("t") ? y + height : y,
    };

    document.body.style.cursor = corner === "tl" ? "nw-resize"
      : corner === "tr" ? "ne-resize"
      : corner === "bl" ? "sw-resize"
      : "se-resize";

    const mouseMoveHandler = (moveEvent) => {
      const container = containerRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const ds = imageSize && imageSize[0] && containerWidth > 0
        ? containerWidth * scale / imageSize[0] : 1;
      const mx = (moveEvent.clientX - rect.left + container.scrollLeft) / ds;
      const my = (moveEvent.clientY - rect.top  + container.scrollTop)  / ds;

      const newBoundingBox = {
        x: Math.min(mx, fixed.x),
        y: Math.min(my, fixed.y),
        width:  Math.max(Math.abs(mx - fixed.x), 10),
        height: Math.max(Math.abs(my - fixed.y), 10),
      };
      const updatedElement = { ...element, boundingBox: newBoundingBox };

      currentElementPositionRef.current = updatedElement;
      setElements(prev => prev.map(el => el._id === element._id ? updatedElement : el));
      setSelectedElement(updatedElement);
    };

    const mouseUpHandler = () => {
      document.body.style.cursor = "";
      const elementToUpdate = currentElementPositionRef.current;
      if (elementToUpdate) {
        api.put(`/api/elements/${elementToUpdate._id}`, { boundingBox: elementToUpdate.boundingBox })
          .then(res => {
            setElements(prev => prev.map(el => el._id === elementToUpdate._id ? res.data : el));
            setSelectedElement(res.data);
          })
          .catch(err => console.error("Error saving element resize:", err));
      }
      document.removeEventListener("mousemove", mouseMoveHandler);
      document.removeEventListener("mouseup", mouseUpHandler);
    };

    document.addEventListener("mousemove", mouseMoveHandler);
    document.addEventListener("mouseup", mouseUpHandler);
  };

  // Handle mouse move during drag operation
  const handleMouseMove = useCallback(
    (e) => {
      if (!draggedElement || !isDragging || !containerRef.current) return;

      // Get container's position and scroll
      const container = containerRef.current;
      const containerRect = container.getBoundingClientRect();
      const scrollLeft = container.scrollLeft;
      const scrollTop = container.scrollTop;

      // Calculate new position in document coordinates
      const x =
        (e.clientX -
          containerRect.left +
          scrollLeft -
          dragOffsetRef.current.x * displayScale) /
        displayScale;
      const y =
        (e.clientY -
          containerRect.top +
          scrollTop -
          dragOffsetRef.current.y * displayScale) /
        displayScale;

      // Update position directly in the element's boundingBox
      const updatedBoundingBox = {
        ...draggedElement.boundingBox,
        x: x,
        y: y,
      };

      // Create a new element with updated position
      const updatedElement = {
        ...draggedElement,
        boundingBox: updatedBoundingBox,
      };

      // Update elements array with the new position
      const updatedElements = elements.map((el) =>
        el._id === draggedElement._id ? updatedElement : el
      );

      // Update state with new element positions
      setElements(updatedElements);
      setDraggedElement(updatedElement); // Also update the dragged element reference
    },
    [draggedElement, isDragging, containerRef, displayScale, elements]
  );

  // Handle mouse up - end of drag operation
  const handleMouseUp = useCallback(() => {
    if (!draggedElement || !isDragging) return;

    // Save the final position to the server
    updateElementPosition(draggedElement, draggedElement.boundingBox);

    // Reset drag state
    setIsDragging(false);
    setDraggedElement(null);

    // Remove event listeners
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);
  }, [draggedElement, isDragging, handleMouseMove]);

  // Clean up event listeners when component unmounts
  useEffect(() => {
    return () => {
      document.body.style.cursor = "";
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  // Update containerWidth whenever the container enters the DOM (after loading completes)
  // and whenever the window resizes
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, [loading, imageLoading]); // re-run when container becomes visible

  // Also update containerWidth when sidebar/maximized changes
  useEffect(() => {
    if (containerRef.current) {
      setContainerWidth(containerRef.current.offsetWidth);
    }
  }, [isMaximized, sidebarOpen]);

  const MONO = "'IBM Plex Mono', monospace";
  // "Processed" = confirmed OR rejected — both are a completed annotation
  // decision during eval/training dataset work, not just confirms.
  const processedEls = elements.filter(e => e.confirmed || e.metadata?.rejected).length;
  const processedConns = connections.filter(c => c.confirmed || c.metadata?.rejected).length;
  const totalProcessed = processedEls + processedConns;
  const totalItems = elements.length + connections.length;
  const verifyPct = totalItems > 0 ? totalProcessed / totalItems : 0;

  const tBtn = {
    display: 'inline-flex', alignItems: 'center', gap: '7px', height: '34px', px: '13px',
    borderRadius: '8px', border: '1px solid rgba(120,150,200,.24)', background: 'transparent',
    color: '#cdd9ee', fontSize: '13px', fontWeight: 500, cursor: 'pointer',
    fontFamily: "'Lexend', sans-serif", transition: 'border-color .15s, color .15s',
    '&:hover': { borderColor: 'rgba(45,212,191,.6)', color: '#fff' },
  };

  // ── Canvas render support ─────────────────────────────────────────────────
  // A large drawing (pandid2: ~2,200 elements, ~940 connections) re-rendered
  // every element and connection on any state change. Element and Connection
  // are memoised; these keep their props stable so only what changed renders:
  // id lookups instead of list scans, and handlers shared by every item that
  // always call the latest handler through a ref.
  const elementsById = useMemo(() => new Map(elements.map(el => [String(el._id), el])), [elements]);
  const connectionsById = useMemo(() => new Map(connections.map(c => [String(c._id), c])), [connections]);
  const connectedToSelected = useMemo(() => {
    const ids = new Set();
    if (!selectedElement) return ids;
    const sel = String(selectedElement._id);
    for (const c of connections) {
      const a = getElementId(c.source), b = getElementId(c.target);
      if (a === sel && b) ids.add(b);
      if (b === sel && a) ids.add(a);
    }
    ids.delete(sel);
    return ids;
  }, [selectedElement, connections]);
  // ── Draw only what's on screen ────────────────────────────────────────────
  // The area to draw, in image coordinates: the visible scroll window plus a
  // margin of half a screen each side so panning doesn't show items popping
  // in. Kept until the view moves a quarter-screen towards its edge, so
  // scrolling re-renders only occasionally. null = draw everything (no layout
  // measured yet).
  const cullRectRef = useRef(null);
  const cullRect = useMemo(() => {
    const v = viewportRect;
    if (!v || !(displayScale > 0) || !v.clientWidth || !v.clientHeight) return null;
    const x0 = v.scrollLeft / displayScale, y0 = v.scrollTop / displayScale;
    const x1 = (v.scrollLeft + v.clientWidth) / displayScale, y1 = (v.scrollTop + v.clientHeight) / displayScale;
    const w = x1 - x0, h = y1 - y0;
    const cur = cullRectRef.current;
    const sameScale = cur && Math.abs(cur.w - w) < 1e-6 * Math.max(w, 1) && Math.abs(cur.h - h) < 1e-6 * Math.max(h, 1);
    // A zoom's widened area (below) is tightened once a settled view comes in.
    if (sameScale && !cur.zooming && x0 >= cur.x0 + w / 4 && y0 >= cur.y0 + h / 4 && x1 <= cur.x1 - w / 4 && y1 <= cur.y1 - h / 4) return cur;
    let rx0 = x0, ry0 = y0, rx1 = x1, ry1 = y1;
    if (!sameScale) {
      // Zoom: the scroll position is corrected a frame later (pendingScrollBBox,
      // or the wheel handler's timeout), so this render's scroll is stale. Also
      // cover where the view is heading — the zoom's target, else the old
      // centre — so nothing goes missing in either frame.
      const target = pendingScrollBBox.current?.bbox;
      const cx = target ? target.x + target.width / 2 : cur ? cur.cx : (x0 + x1) / 2;
      const cy = target ? target.y + target.height / 2 : cur ? cur.cy : (y0 + y1) / 2;
      rx0 = Math.min(rx0, cx - w / 2); ry0 = Math.min(ry0, cy - h / 2);
      rx1 = Math.max(rx1, cx + w / 2); ry1 = Math.max(ry1, cy + h / 2);
    }
    const next = { x0: rx0 - w / 2, y0: ry0 - h / 2, x1: rx1 + w / 2, y1: ry1 + h / 2, w, h, cx: (x0 + x1) / 2, cy: (y0 + y1) / 2, zooming: !sameScale };
    cullRectRef.current = next;
    return next;
  }, [viewportRect, displayScale]);
  const onScreen = (b) => !cullRect || !b
    || !(b.x > cullRect.x1 || b.x + b.width < cullRect.x0 || b.y > cullRect.y1 || b.y + b.height < cullRect.y0);
  // Each connection's extent (image coords): its drawn path, else its two ends.
  const connectionBoxes = useMemo(() => {
    const boxes = new Map();
    const extent = (pts) => {
      const xs = pts.map(p => p[0]), ys = pts.map(p => p[1]);
      const x = Math.min(...xs), y = Math.min(...ys);
      return { x, y, width: Math.max(...xs) - x, height: Math.max(...ys) - y };
    };
    const centre = (b) => [b.x + b.width / 2, b.y + b.height / 2];
    for (const c of connections) {
      const path = c.metadata?.path_data?.path;
      if (Array.isArray(path) && path.length) {
        boxes.set(String(c._id), extent(path.flatMap(seg => [seg.start, seg.end])));
        continue;
      }
      const a = elementsById.get(getElementId(c.source))?.boundingBox;
      const b = elementsById.get(getElementId(c.target))?.boundingBox;
      // A text label linked to a pipe ends somewhere on that pipe: no box, always drawn.
      if (a && b) boxes.set(String(c._id), extent([centre(a), centre(b)]));
    }
    return boxes;
  }, [connections, elementsById]);

  const overlayElements = useMemo(() => elements.filter(el => !(hideRejected && el.metadata?.rejected) && !(hideTraces && isTraceElement(el))),
    [elements, hideRejected, hideTraces]);
  const overlayConnections = useMemo(() => connections.filter(c => !(hideRejected && c.metadata?.rejected) && !(hideTraces && isTraceConnection(c))),
    [connections, hideRejected, hideTraces]);

  const canvasHandlersRef = useRef({});
  canvasHandlersRef.current = { handleElementMouseDown, handleCornerMouseDown, handleConnectionSelect, handleEndpointHandleMouseDown, handleVertexMouseDown };
  const onCanvasElementMouseDown = useCallback((e, el) => canvasHandlersRef.current.handleElementMouseDown(e, el), []);
  const onCanvasCornerMouseDown = useCallback((e, el, corner) => canvasHandlersRef.current.handleCornerMouseDown(e, el, corner), []);
  const onCanvasConnectionClick = useCallback((e, c) => { e.stopPropagation(); canvasHandlersRef.current.handleConnectionSelect(c); }, []);
  const onCanvasEndpointMouseDown = useCallback((which, e, c) => canvasHandlersRef.current.handleEndpointHandleMouseDown(c, which, e), []);
  const onCanvasVertexMouseDown = useCallback((i, e, c) => canvasHandlersRef.current.handleVertexMouseDown(c, i, e), []);
  const onCanvasPickHover = useCallback((elementId) => setHoverElementId(elementId), []);

  // Claude pipeline: trace lines from the symbols as currently corrected.
  const pipeline = drawing?.metadata?.pipeline;

  // ── Contact sheet review (components/ContactSheet.js) ─────────────────────
  // A second view of the drawing: every symbol as a tile, grouped by class, for
  // confirming classes in bulk. It opens by itself once symbol detection has
  // finished and lines aren't traced yet (pipeline stage 'symbols'): reviewing
  // the symbols first is recommended, as lines are traced between them.
  const [viewMode, setViewMode] = useState('drawing');
  const sheetDecidedForRef = useRef(null);   // the pipeline stage the opening view was chosen for
  useEffect(() => {
    if (!linksReady || !drawing) return;
    const stage = pipeline?.stage || 'none';
    if (sheetDecidedForRef.current === stage) return;
    const first = sheetDecidedForRef.current === null;
    sheetDecidedForRef.current = stage;
    const linked = parseItemPath(itemPath) || new URLSearchParams(location.search).get('view');
    if (stage === 'symbols' && pipeline?.name === 'claude' && !isGroundTruthDrawing && !(first && linked)) setViewMode('sheet');
  }, [linksReady, drawing, pipeline?.stage]); // eslint-disable-line react-hooks/exhaustive-deps

  const showSheet = useCallback(() => {
    if (isRapidMode) exitRapidMode();
    if (isCreatingElement) handleCancelElementCreation();
    setSelectedElement(null);
    setSelectedConnection(null);
    setPreviewItem(null);
    setViewMode('sheet');
  }, [isRapidMode, isCreatingElement]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSheetOpenInDrawing = useCallback((element) => {
    setViewMode('drawing');
    setSelectedConnection(null);
    setSelectedElement(element);
  }, []);

  const mergeElements = useCallback((updated) => {
    const byId = new Map(updated.map(el => [String(el._id), el]));
    setElements(els => els.map(el => byId.get(String(el._id)) || el));
  }, []);

  const handleSheetBulkUpdate = useCallback(async (ids, set, what) => {
    // Shown at once (the progress bar jumps on the click), saved behind it, put
    // back if the save fails.
    const wanted = new Set(ids.map(String));
    let before = [];
    setElements(els => {
      before = els.filter(el => wanted.has(String(el._id)));
      return els.map(el => (wanted.has(String(el._id))
        ? { ...el, ...(set.elementType !== undefined ? { elementType: set.elementType, typeConfirmed: true } : {}),
            ...(set.typeConfirmed !== undefined ? { typeConfirmed: set.typeConfirmed } : {}) }
        : el));
    });
    try {
      const { data } = await api.patch(`/api/drawings/${id}/elements/bulk`, { ids, set });
      mergeElements(data.elements);
      const n = ids.length;
      const text = set.lineStyle
        ? `Line style ${set.lineStyle.replace('_', ' ')} on ${n} instrument${n === 1 ? '' : 's'}`
        : set.elementType
        ? `${n} symbol${n === 1 ? '' : 's'} → ${getLabelForType(set.elementType)}`
        : `Confirmed ${what || `${n} symbol type${n === 1 ? '' : 's'}`}`;
      notify(text, { undo: async () => {
        try {
          for (const call of (set.lineStyle ? lineStyleUndoCalls : undoCalls)(data.previous)) {
            const { data: back } = await api.patch(`/api/drawings/${id}/elements/bulk`, call);
            mergeElements(back.elements);
          }
        } catch (err) {
          notify(`Undo failed: ${err.response?.data?.msg || err.message}`, { tone: 'error' });
        }
      } });
    } catch (err) {
      mergeElements(before);
      notify(`Could not update the symbols: ${err.response?.data?.msg || err.message}`, { tone: 'error' });
    }
  }, [id, mergeElements, notify]);

  // The contact sheet's instrument panel: tag lines, line style, annotations.
  const handleSheetSaveInstrument = useCallback(async (element, payload) => {
    try {
      const { data } = await api.put(`/api/elements/${element._id}`, payload);
      mergeElements([data]);
    } catch (err) {
      notify(`Could not save the instrument: ${err.response?.data?.msg || err.message}`, { tone: 'error' });
    }
  }, [mergeElements, notify]);

  // After the drawing's instrument conventions change, the server has reparsed its instruments.
  const handleSheetRefreshElements = useCallback(async () => {
    try {
      const { data } = await api.get(`/api/drawings/${id}/elements`);
      mergeElements(data);
    } catch (err) {
      notify(`Could not reload the symbols: ${err.response?.data?.msg || err.message}`, { tone: 'error' });
    }
  }, [id, mergeElements, notify]);

  // Rejecting an element rejects its text links too (server-side); keep the
  // connections in step, as the side pane does.
  const applySheetLinkChanges = useCallback((linkChanges, rejected) => {
    if (!linkChanges?.length) return;
    const byId = new Map(linkChanges.map(ch => [String(ch._id), ch]));
    setConnections(cs => cs.map(c => {
      const ch = byId.get(String(c._id));
      if (!ch) return c;
      const { rejectionReason, rejectedWith, ...meta } = c.metadata || {};
      return { ...c, metadata: ch.rejected ? { ...meta, rejected: true, rejectionReason: ch.rejectionReason } : { ...meta, rejected: false } };
    }));
  }, []);

  const handleSheetReject = useCallback(async (ids) => {
    const reject = async (rejected) => {
      const { data } = await api.patch(`/api/drawings/${id}/elements/bulk`, { ids, set: rejected ? { rejected, rejectionReason: 'false_positive_shapes' } : { rejected } });
      mergeElements(data.elements);
      applySheetLinkChanges(data.linkChanges, rejected);
    };
    try {
      await reject(true);
      notify(`Rejected ${ids.length} symbol${ids.length === 1 ? '' : 's'} as false detections`, { undo: () => reject(false).catch(err =>
        notify(`Undo failed: ${err.response?.data?.msg || err.message}`, { tone: 'error' })) });
    } catch (err) {
      notify(`Could not reject: ${err.response?.data?.msg || err.message}`, { tone: 'error' });
    }
  }, [id, mergeElements, applySheetLinkChanges, notify]);

  const handleSheetDelete = useCallback(async (ids) => {
    if (!window.confirm(`Delete ${ids.length} symbol${ids.length === 1 ? '' : 's'}? They aren't symbols.`)) return;
    const gone = new Set();
    for (const elId of ids) {
      try { await api.delete(`/api/elements/${elId}`); gone.add(String(elId)); } catch (err) { /* reported below */ }
    }
    setElements(els => els.filter(el => !gone.has(String(el._id))));
    setConnections(cs => cs.filter(c => !gone.has(getElementId(c.source)) && !gone.has(getElementId(c.target))));
    notify(gone.size === ids.length ? `Deleted ${gone.size} symbol${gone.size === 1 ? '' : 's'}` : `Deleted ${gone.size} of ${ids.length} — some could not be deleted`,
      { tone: gone.size === ids.length ? 'success' : 'error' });
  }, [notify]);
  const handleTraceLines = useCallback(async () => {
    try {
      await api.post(`/api/drawings/${id}/trace-lines`);
      setIsProcessing(true);
      setProcessingMessage('Tracing lines…');
      startPollingProcessingStatus();
    } catch (err) {
      notify(`Could not start line tracing: ${err.response?.data?.msg || err.message}`, { tone: 'error' });
    }
  }, [id, startPollingProcessingStatus, notify]);

  // Checkpoint complete: hide steering, resume polling as a backstop in case
  // the socket connection was dropped (e.g. navigated away and back) and
  // never delivers the terminal 'completed' event.
  const handleCheckpointComplete = useCallback(() => {
    setShowCheckpoint(false);
    setIsProcessing(true);
    setProcessingMessage('Pipeline resuming…');
    startPollingProcessingStatus();
  }, [startPollingProcessingStatus]);

  return (
    <>
    {/* Checkpoint steering overlay — shown when pipeline paused at checkpoint */}
    {showCheckpoint && (
      <CheckpointSteering
        drawing={drawing}
        imageBlobUrl={imageBlobUrl}
        onComplete={handleCheckpointComplete}
      />
    )}
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', background: '#0a1326', overflow: 'hidden' }}>

      {/* ── SLIM APP NAV ── */}
      {!isMaximized && (
        <Box component="nav" sx={{ flex: 'none', display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: '20px', height: '52px', background: '#070e1c', borderBottom: '1px solid rgba(120,150,200,.16)', zIndex: 10 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '18px', minWidth: 0 }}>
            <Box component="a" href="/dashboard" sx={{ display: 'flex', alignItems: 'center', gap: '8px', textDecoration: 'none', color: '#8fa3c6', fontSize: '13px', '&:hover': { color: '#eaf0fb' } }}>
              <Box component="svg" viewBox="0 0 24 24" sx={{ width: 16, height: 16, fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' }}>
                <path d="M15 18l-6-6 6-6" />
              </Box>
              <Box component="span" sx={{ fontFamily: MONO, fontWeight: 600, letterSpacing: '.2em', color: '#2dd4bf' }}>PANDID</Box>
            </Box>
            <Box sx={{ width: '1px', height: '20px', background: 'rgba(120,150,200,.2)' }} />
            {drawing && (
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '10px', minWidth: 0 }}>
                <Box sx={{ fontSize: '14px', fontWeight: 600, color: '#f4f8ff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '280px' }}>{drawing.name}</Box>
              </Box>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px', minWidth: 0 }}>
            {/* Review progress — up here to leave the toolbar room (not shown in full view) */}
            {elements.length > 0 && (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', fontFamily: MONO, fontSize: '11.5px', userSelect: 'none' }}>
                    <Box component="span" sx={{ color: '#9fb2d4' }}>EL</Box>
                    <Box component="span" sx={{ color: '#7fe0a6' }}>{processedEls}<Box component="span" sx={{ color: '#5f7398' }}>/{elements.length}</Box></Box>
                    <Box component="span" sx={{ color: '#3a4d70' }}>·</Box>
                    <Box component="span" sx={{ color: '#9fb2d4' }}>CONN</Box>
                    <Box component="span" sx={{ color: '#7fe0a6' }}>{processedConns}<Box component="span" sx={{ color: '#5f7398' }}>/{connections.length}</Box></Box>
                  </Box>
                  <Box sx={{ position: 'relative', width: '130px', height: '6px', borderRadius: '999px', background: 'rgba(120,150,200,.18)', overflow: 'hidden', flexShrink: 0, '@media (max-width: 1700px)': { display: 'none' } }}>
                    <Box sx={{ position: 'absolute', inset: 0, right: `${(1 - verifyPct) * 100}%`, background: 'linear-gradient(90deg,#2dd4bf,#44d07b)', borderRadius: '999px', transition: 'right .3s' }} />
                  </Box>
                  <Box sx={{ fontFamily: MONO, fontSize: '12px', color: '#7fe0a6' }}>{Math.round(verifyPct * 100)}%</Box>
                </>
              )}
            <HeaderNotifications queue={notifications} onDismiss={dismissNotification} onUndo={undoNotification} />
          </Box>
        </Box>
      )}

      {/* ── TOOLBAR ── */}
      {!loading && !imageLoading && !error && (
        <Box sx={{ flex: 'none', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '14px', px: '16px', height: '56px', background: '#0d1a33', borderBottom: '1px solid rgba(120,150,200,.16)' }}>
          {/* left */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {/* Drawing | Contact sheet */}
            <Box role="tablist" aria-label="View" sx={{ display: 'flex', border: '1px solid rgba(120,150,200,.28)', borderRadius: '8px', overflow: 'hidden', flexShrink: 0 }}>
              {[['drawing', 'Drawing'], ['sheet', 'Contact sheet']].map(([mode, label]) => (
                <Box key={mode} component="button" role="tab" aria-selected={viewMode === mode}
                  onClick={() => (mode === 'sheet' ? showSheet() : setViewMode('drawing'))}
                  title={mode === 'sheet' ? 'Every symbol as a tile, grouped by class — confirm classes in bulk, fix the odd ones out' : 'The drawing, with the element and connection list'}
                  sx={{ height: '30px', px: '12px', border: 'none', cursor: 'pointer', fontSize: '12.5px', fontFamily: "'Lexend', sans-serif", whiteSpace: 'nowrap',
                    background: viewMode === mode ? 'rgba(45,212,191,.18)' : 'transparent', color: viewMode === mode ? '#2dd4bf' : '#8fa3c6' }}>
                  {label}
                </Box>
              ))}
            </Box>
            <Box sx={{ display: viewMode === 'sheet' ? 'none' : 'contents' }}>
            {/* Show all toggle */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
              <Box component="span" onClick={() => setShowAllObjects(v => !v)}
                sx={{ position: 'relative', width: '38px', height: '21px', borderRadius: '999px', background: showAllObjects ? 'rgba(45,212,191,.9)' : 'rgba(120,150,200,.22)', cursor: 'pointer', flexShrink: 0, transition: 'background .15s' }}>
                <Box sx={{ position: 'absolute', top: '2px', left: showAllObjects ? '19px' : '2px', width: '17px', height: '17px', borderRadius: '50%', background: showAllObjects ? '#06121f' : '#7f93b8', transition: 'left .15s' }} />
              </Box>
              <Box sx={{ fontSize: '13px', color: showAllObjects ? '#dce6fa' : '#8fa3c6', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }} onClick={() => setShowAllObjects(v => !v)}>Show all</Box>
            </Box>
            {/* Show unlinked toggle — last-mile graph clean-up, for everyone */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
              <Box component="span" onClick={() => setShowUnlinked(v => !v)}
                sx={{ position: 'relative', width: '38px', height: '21px', borderRadius: '999px', background: showUnlinked ? 'rgba(56,189,248,.85)' : 'rgba(120,150,200,.22)', cursor: 'pointer', flexShrink: 0, transition: 'background .15s' }}>
                <Box sx={{ position: 'absolute', top: '2px', left: showUnlinked ? '19px' : '2px', width: '17px', height: '17px', borderRadius: '50%', background: showUnlinked ? '#04202e' : '#7f93b8', transition: 'left .15s' }} />
              </Box>
              <Box sx={{ fontSize: '13px', color: showUnlinked ? '#9adcfb' : '#8fa3c6', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }} onClick={() => setShowUnlinked(v => !v)}><Box component="span" sx={{ '@media (max-width: 1500px)': { display: 'none' } }}>Show unlinked</Box><Box component="span" sx={{ display: 'none', '@media (max-width: 1500px)': { display: 'inline' } }}>Unlinked</Box></Box>
              {unlinked && (unlinked.ordered.length > 0 ? (
                <Box component="button" onClick={handleNextUnlinked} title="Not yet wired into the graph — click to jump to the next one"
                  sx={{ border: '1px solid rgba(56,189,248,.5)', background: 'rgba(56,189,248,.08)', color: '#38bdf8', borderRadius: '6px', px: '7px', py: '2px', fontFamily: MONO, fontSize: '11.5px', whiteSpace: 'nowrap', cursor: 'pointer', '&:hover': { background: 'rgba(56,189,248,.16)' } }}>
                  {unlinked.ordered.length} unlinked →
                </Box>
              ) : (
                <Box component="span" sx={{ color: '#7fe0a6', fontFamily: MONO, fontSize: '11.5px', whiteSpace: 'nowrap' }}>✓ all linked</Box>
              ))}
            </Box>
            {/* Hide rejected toggle — admin/annotation tooling only (§0.2 item 7) */}
            {isAdmin && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
                <Box component="span" onClick={() => setHideRejected(v => !v)}
                  sx={{ position: 'relative', width: '38px', height: '21px', borderRadius: '999px', background: hideRejected ? 'rgba(244,113,106,.75)' : 'rgba(120,150,200,.22)', cursor: 'pointer', flexShrink: 0, transition: 'background .15s' }}>
                  <Box sx={{ position: 'absolute', top: '2px', left: hideRejected ? '19px' : '2px', width: '17px', height: '17px', borderRadius: '50%', background: hideRejected ? '#2a0906' : '#7f93b8', transition: 'left .15s' }} />
                </Box>
                <Box sx={{ fontSize: '13px', color: hideRejected ? '#f4a39e' : '#8fa3c6', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }} onClick={() => setHideRejected(v => !v)}>Hide rejected</Box>
              </Box>
            )}
            {/* Traces toggle — admin: show / hide traced lines (not on ground-truth drawings) */}
            {isAdmin && !isGroundTruthDrawing && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '9px' }} title="Show or hide traced pipe and signal lines and junctions — ones you drew or edited always show (as does a selected one)">
                <Box component="span" onClick={() => setShowTraces(v => !v)}
                  sx={{ position: 'relative', width: '38px', height: '21px', borderRadius: '999px', background: showTracesPref ? 'rgba(45,212,191,.9)' : 'rgba(120,150,200,.22)', cursor: 'pointer', flexShrink: 0, transition: 'background .15s' }}>
                  <Box sx={{ position: 'absolute', top: '2px', left: showTracesPref ? '19px' : '2px', width: '17px', height: '17px', borderRadius: '50%', background: showTracesPref ? '#06121f' : '#7f93b8', transition: 'left .15s' }} />
                </Box>
                <Box sx={{ fontSize: '13px', color: showTracesPref ? '#dce6fa' : '#8fa3c6', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }} onClick={() => setShowTraces(v => !v)}>Traces</Box>
              </Box>
            )}
            <Box sx={{ width: '1px', height: '26px', background: 'rgba(120,150,200,.18)', mx: '4px' }} />
            <Box component="button" onClick={handleAddElement} disabled={isProcessing}
              title={isCreatingElement ? 'Click again to cancel' : 'Draw a new element'}
              sx={{ ...tBtn, borderColor: isCreatingElement ? 'rgba(45,212,191,.6)' : 'rgba(120,150,200,.24)', color: isCreatingElement ? '#2dd4bf' : '#cdd9ee', background: isCreatingElement ? 'rgba(45,212,191,.1)' : 'transparent', '&:disabled': { opacity: .4, cursor: 'default' } }}>
              <Box component="svg" viewBox="0 0 24 24" sx={{ width: 15, height: 15, fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round' }}><path d="M12 5v14M5 12h14" /></Box>
              Element
            </Box>
            <Box component="button" onClick={handleAddConnection} disabled={isProcessing || elements.length < 2}
              title={isCreatingConnection ? 'Click again to cancel' : 'Draw a new connection'}
              sx={{ ...tBtn, borderColor: isCreatingConnection ? 'rgba(45,212,191,.6)' : 'rgba(120,150,200,.24)', color: isCreatingConnection ? '#2dd4bf' : '#cdd9ee', background: isCreatingConnection ? 'rgba(45,212,191,.1)' : 'transparent', '&:disabled': { opacity: .4, cursor: 'default' } }}>
              <Box component="svg" viewBox="0 0 24 24" sx={{ width: 15, height: 15, fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' }}>
                <circle cx="6" cy="6" r="2.4" /><circle cx="18" cy="18" r="2.4" /><path d="M8 7.5 16 16.5" />
              </Box>
              Connection
            </Box>
            {isAdmin && (
              <>
                {[['symbol', 'Symbol', 'Symbol: click symbols, AI detection identifies them'], ['text', 'Text', 'Text: drag boxes around text, OCR reads it']].map(([tool, label, hint]) => (
                  <Box key={tool} component="button" onClick={() => handleRapidToolToggle(tool)} disabled={isProcessing}
                    title={rapidTool === tool ? `Exit ${label.toLowerCase()} annotate (Esc)` : hint}
                    sx={{ ...tBtn, borderColor: rapidTool === tool ? `${VLM_COLORS.pending}99` : 'rgba(120,150,200,.24)', color: rapidTool === tool ? VLM_COLORS.pending : '#cdd9ee', background: rapidTool === tool ? 'rgba(167,139,250,.1)' : 'transparent', '&:disabled': { opacity: .4, cursor: 'default' } }}>
                    <Box component="svg" viewBox="0 0 24 24" sx={{ width: 15, height: 15, fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' }}>
                      <path d="M13 2 4 14h7l-1 8 9-12h-7z" />
                    </Box>
                    {label}
                  </Box>
                ))}
                {(vlmCounts.pending > 0 || vlmCounts.review > 0 || vlmCounts.failed > 0) && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', fontFamily: MONO, fontSize: '11.5px', userSelect: 'none' }}>
                    {vlmCounts.pending > 0 && (
                      <Box component="span" title="AI detection in progress" sx={{ color: VLM_COLORS.pending }}>⋯ {vlmCounts.pending}</Box>
                    )}
                    {vlmCounts.review > 0 && (
                      <Box component="button" onClick={handleNextToReview} title="Detected, awaiting your confirm — click to jump to the next one"
                        sx={{ border: `1px solid ${VLM_COLORS.review}80`, background: 'rgba(245,165,36,.08)', color: VLM_COLORS.review, borderRadius: '6px', px: '7px', py: '2px', fontFamily: MONO, fontSize: '11.5px', cursor: 'pointer', '&:hover': { background: 'rgba(245,165,36,.16)' } }}>
                        {vlmCounts.review} to review →
                      </Box>
                    )}
                    {vlmCounts.failed > 0 && (
                      <Box component="span" title="AI detection failed on these — select one to retry" sx={{ color: VLM_COLORS.failed }}>! {vlmCounts.failed}</Box>
                    )}
                  </Box>
                )}
              </>
            )}
            </Box>
          </Box>

          {/* right */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            {/* Trace / re-trace lines (drawing processing's second step). Not on
                ground-truth drawings (EVAL- / TRAINING-): their pipes are drawn by
                hand; the eval re-runs tracing itself to score it. */}
            {pipeline?.name === 'claude' && !isProcessing && !isGroundTruthDrawing && (
              <>
                <Box component="button" onClick={handleTraceLines}
                  title={pipeline.stage === 'symbols'
                    ? `Drawing processing found ${pipeline.symbols ?? 0} symbols (${pipeline.named ?? 0} named from their tags) and ${pipeline.texts ?? 0} text labels. Check and correct the symbols, then trace the lines between them.`
                    : "Lines traced from the symbols as they were. Traced lines and junctions are unconfirmed — check flow direction as you confirm them. Re-tracing replaces any you haven't confirmed."}
                  sx={{ ...tBtn, whiteSpace: 'nowrap', fontWeight: 600,
                    ...(pipeline.stage === 'symbols'
                      ? { background: '#2dd4bf', borderColor: '#2dd4bf', color: '#04211d', '&:hover': { opacity: 0.9 } }
                      : { borderColor: 'rgba(45,212,191,.6)', color: '#2dd4bf' }) }}>
                  {pipeline.stage === 'symbols' ? 'Trace lines' : 'Re-trace lines'}
                </Box>
                <Box sx={{ width: '1px', height: '26px', background: 'rgba(120,150,200,.18)' }} />
              </>
            )}
            {/* zoom controls: the drawing's, not the contact sheet's */}
            <Box sx={{ display: viewMode === 'sheet' ? 'none' : 'contents' }}>
            {/* zoom tool toggle */}
            <Box component="button" onClick={handleZoomModeToggle} disabled={isProcessing} title="Zoom tool (drag to zoom to area, click to zoom in, right-click to zoom out, Esc to exit)"
              sx={{ ...tBtn, borderColor: isZoomMode ? 'rgba(99,179,237,.6)' : 'rgba(120,150,200,.24)', color: isZoomMode ? '#63b3ed' : '#cdd9ee', background: isZoomMode ? 'rgba(99,179,237,.1)' : 'transparent', '&:disabled': { opacity: .4, cursor: 'default' } }}>
              <Box component="svg" viewBox="0 0 24 24" sx={{ width: 15, height: 15, fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' }}>
                <circle cx="11" cy="11" r="7" />
                <path d="M16.5 16.5 21 21" />
                <path d="M8 11h6M11 8v6" />
              </Box>
            </Box>
            {/* zoom */}
            <Box sx={{ display: 'flex', alignItems: 'center', border: '1px solid rgba(120,150,200,.24)', borderRadius: '8px', overflow: 'hidden' }}>
              <Box component="button" onClick={handleZoomOut} disabled={scale <= MIN_SCALE} aria-label="Zoom out"
                sx={{ border: 'none', background: 'transparent', color: '#cdd9ee', width: '34px', height: '34px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', cursor: 'pointer', '&:hover': { background: 'rgba(120,150,200,.1)' }, '&:disabled': { opacity: .4, cursor: 'default' } }}>−</Box>
              <Box data-testid="zoom-level" sx={{ fontFamily: MONO, fontSize: '12px', color: '#cdd9ee', px: '10px', borderLeft: '1px solid rgba(120,150,200,.18)', borderRight: '1px solid rgba(120,150,200,.18)', height: '34px', display: 'flex', alignItems: 'center', minWidth: '52px', justifyContent: 'center', userSelect: 'none' }}>{Math.round(scale * 100)}%</Box>
              <Box component="button" onClick={handleZoomIn} aria-label="Zoom in"
                sx={{ border: 'none', background: 'transparent', color: '#cdd9ee', width: '34px', height: '34px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', cursor: 'pointer', '&:hover': { background: 'rgba(120,150,200,.1)' } }}>+</Box>
            </Box>
            <Box component="button" onClick={() => setScale(1)} title="Reset zoom"
              sx={{ ...tBtn, width: '34px', px: 0, justifyContent: 'center' }}>
              <FindReplaceIcon sx={{ fontSize: '15px' }} />
            </Box>
            </Box>
            <Box component="button" onClick={() => setIsMaximized(!isMaximized)} title={isMaximized ? 'Exit full view' : 'Maximize'}
              sx={{ ...tBtn, width: '34px', px: 0, justifyContent: 'center' }}>
              {isMaximized ? <FullscreenExitIcon sx={{ fontSize: '15px' }} /> : <FullscreenIcon sx={{ fontSize: '15px' }} />}
            </Box>
          </Box>
        </Box>
      )}


      {/* ── RAPID ANNOTATE BANNER ── */}
      {isRapidMode && (
        <Box sx={{ flex: 'none', display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: '20px', py: '8px', background: 'rgba(167,139,250,.08)', borderBottom: '1px solid rgba(167,139,250,.3)', fontFamily: "'Lexend', sans-serif", fontSize: '13px', color: '#c4b5fd' }}>
          <Box>
            {rapidTool === 'text'
              ? <>Drag a box around text to read it · click an existing text label to select it · Backspace undoes the last placement · Esc exits</>
              : rapidBoxSize
                ? <>Click a symbol to place a {Math.round(rapidBoxSize.width)}×{Math.round(rapidBoxSize.height)} box · drag to place a custom box (sets the new size) · click an existing box to select it · Backspace undoes the last placement · Esc exits</>
                : <>Drag a box around one symbol to start — its size becomes the click size.</>}
          </Box>
          <Box component="button" onClick={exitRapidMode}
            sx={{ background: 'transparent', border: '1px solid rgba(167,139,250,.5)', color: '#c4b5fd', fontSize: '12px', px: '12px', py: '5px', borderRadius: '7px', cursor: 'pointer', fontFamily: "'Lexend', sans-serif", flexShrink: 0, ml: '12px', '&:hover': { background: 'rgba(167,139,250,.12)' } }}>
            Done
          </Box>
        </Box>
      )}

      {/* ── CANVAS MODE BANNER ── */}
      {(canvasPickMode || pendingEndpoint) && (
        <Box sx={{ flex: 'none', display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: '20px', py: '10px', background: 'rgba(45,212,191,.1)', borderBottom: '1px solid rgba(45,212,191,.3)', fontFamily: "'Lexend', sans-serif", fontSize: '13px', color: '#2dd4bf' }}>
          <Box>
            {pendingEndpoint
              ? `Click on the drawing to place the new ${pendingEndpoint.which === 'source' ? 'FROM' : 'TO'} element. Draw a bounding box (two clicks).`
              : `Click an element on the drawing to set as ${canvasPickMode?.which === 'source' ? 'source' : 'target'}.`}
          </Box>
          <Box component="button"
            onClick={() => { handleExitCanvasPickMode(); setPendingEndpoint(null); handleCancelElementCreation(); }}
            sx={{ background: 'transparent', border: '1px solid rgba(45,212,191,.5)', color: '#2dd4bf', fontSize: '12px', px: '12px', py: '5px', borderRadius: '7px', cursor: 'pointer', fontFamily: "'Lexend', sans-serif", '&:hover': { background: 'rgba(45,212,191,.12)' } }}>
            Cancel
          </Box>
        </Box>
      )}

      {/* ── CONTENT ── */}
      <Box sx={{ flex: 1, display: 'flex', minHeight: 0, overflow: 'hidden' }}>
        {(loading || imageLoading) ? (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress sx={{ color: '#2dd4bf' }} />
          </Box>
        ) : error ? (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: '32px' }}>
            <Alert severity="error" sx={{ background: 'rgba(244,113,106,.1)', color: '#f4a39e', border: '1px solid rgba(244,113,106,.3)', '& .MuiAlert-icon': { color: '#f4716a' } }}>
              {error}
            </Alert>
          </Box>
        ) : (
          <>
            {/* canvas wrapper — positions the scroll container + floating overlays */}
            <Box sx={{ flex: 1, position: 'relative', minWidth: 0 }}>
              {(isProcessing || processingStatus === 'processing') && (
                <ProcessingOverlay message={processingMessage} />
              )}
              {viewMode === 'sheet' && !(isProcessing || processingStatus === 'processing') && (
                <Box sx={{ position: 'absolute', inset: 0, zIndex: 20 }}>
                  <ContactSheet
                    elements={elements}
                    imageBlobUrl={imageBlobUrl}
                    isAdmin={isAdmin}
                    onBulkUpdate={handleSheetBulkUpdate}
                    onReject={handleSheetReject}
                    onDelete={handleSheetDelete}
                    onOpenInDrawing={handleSheetOpenInDrawing}
                    drawingId={id}
                    onSaveInstrument={handleSheetSaveInstrument}
                    onRefreshElements={handleSheetRefreshElements}
                    traceHint={pipeline?.name === 'claude' && pipeline?.stage === 'symbols' && !isGroundTruthDrawing ? { onTrace: handleTraceLines } : null}
                  />
                </Box>
              )}
              <DrawingMinimap
                imageBlobUrl={imageBlobUrl}
                imageSize={imageSize}
                containerRef={containerRef}
                scale={scale}
                sliderValue={scaleToSliderT(scale)}
                viewportRect={viewportRect}
                onJumpTo={scrollToBBox}
                onZoomSlider={handleMinimapZoomSlider}
              />
              <CanvasStackPicker
                stackPicker={stackPicker}
                onPick={handleStackPickerPick}
                onClose={() => setStackPicker(null)}
              />
              <Box
                id="p&id-viewer-container"
                ref={containerRef}
                onMouseDownCapture={handleStackPickerMouseDownCapture}
                onClickCapture={handleStackPickerClickCapture}
                sx={{
                  position: 'absolute', inset: 0,
                  overflow: 'auto',
                  background: '#10182b',
                  backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(120,150,200,.10) 1px, transparent 0)',
                  backgroundSize: '26px 26px',
                  cursor: isDragging ? 'grabbing' : isZoomMode ? (isZoomDraggingRef.current ? 'zoom-out' : 'zoom-in') : isCreatingElement || isRapidMode ? 'crosshair' : canvasPickMode ? 'crosshair' : isCreatingConnection ? 'crosshair' : 'default',
                }}
                onMouseDown={(e) => (isRapidMode ? handleRapidMouseDown(e) : handleZoomMouseDown(e))}
                onMouseMove={(e) => (isRapidMode ? handleRapidMouseMove(e) : handleZoomMouseMove(e))}
                onMouseUp={(e) => (isRapidMode ? handleRapidMouseUp(e) : handleZoomMouseUp(e))}
                onMouseLeave={() => { if (isRapidMode) { setRapidMouse(null); setRapidDrag(null); } }}
                onContextMenu={handleZoomContextMenu}
                onClick={(e) => {
                  if (isZoomMode || isRapidMode) return;
                  handleDrawingAreaClick(e);
                  if (!isCreatingElement && e.target === e.currentTarget) {
                    setSelectedElement(null);
                    setSelectedConnection(null);
                  }
                }}
                onDoubleClick={(e) => {
                  if (isZoomMode || isRapidMode) return;
                  if (e.shiftKey) { setScale((prev) => Math.max(MIN_SCALE, prev - 0.2)); }
                  else { setScale((prev) => Math.min(MAX_SCALE, prev + 0.2)); }
                }}
              >
                {imageBlobUrl && (
                  <img
                    src={imageBlobUrl}
                    alt={drawing?.name}
                    style={{ width: containerWidth > 0 ? containerWidth * scale : "100%", height: "auto", display: "block", userSelect: "none", pointerEvents: "none", position: "relative", zIndex: 1 }}
                    draggable={false}
                    onLoad={(e) => { if (containerRef.current) setContainerWidth(containerRef.current.offsetWidth); setNaturalImageSize([e.target.naturalWidth, e.target.naturalHeight]); }}
                    onError={() => { setError("Failed to display the drawing image. Please try reloading the page."); setImageBlobUrl(null); }}
                  />
                )}

                {containerWidth > 0 && imageSize && imageSize[0] > 0 && (
                  <Box id="p&id-overlay" sx={{ position: "absolute", top: 0, left: 0, width: containerWidth * scale, height: imageSize[1] * displayScale, pointerEvents: ((isCreatingElement && !canvasPickMode) || isZoomMode || isRapidMode) ? 'none' : 'auto', zIndex: 2 }}>
                    {(() => {
                      const selectedElementId = selectedElement ? String(selectedElement._id) : null;
                      // Canvas pick mode: which element is the fixed (non-editable) endpoint
                      const pickConn = canvasPickMode ? connectionsById.get(String(canvasPickMode.connectionId)) : null;
                      const fixedPickId = pickConn
                        ? String(getElementId(canvasPickMode.which === 'source' ? pickConn.target : pickConn.source))
                        : null;
                      return elements.map((element) => {
                      const elementId = String(element._id);
                      const isConnectedToSelectedElement = connectedToSelected.has(elementId);
                      const isPartOfSelectedConnection = Boolean(selectedConnection && (getElementId(selectedConnection.source) === elementId || getElementId(selectedConnection.target) === elementId));
                      const isUnlinked = Boolean(unlinked?.elements.has(elementId));
                      const isInUse = (selectedElement && selectedElementId === elementId) || isConnectedToSelectedElement || isPartOfSelectedConnection
                        || draggedElement?._id === element._id || elementId === fixedPickId;
                      // A previewed list item shows even when hidden (rejected, a trace,
                      // "show all" off): the user picked it out to look at.
                      const isPreviewed = !selectedElement && !selectedConnection && previewItem?.kind === 'element' && previewItem.id === elementId;
                      const isVisible = isPreviewed || (selectedElement && selectedElementId === elementId) || (Boolean(canvasPickMode || showAllObjects || isRapidMode || isUnlinked || (selectedElement && selectedElementId === elementId) || isConnectedToSelectedElement || isPartOfSelectedConnection) && !(hideRejected && element.metadata?.rejected) && !(hideTraces && isTraceElement(element) && !(selectedElement && selectedElementId === elementId))
                        && (isInUse || onScreen(element.boundingBox)));

                      const isFixedEndpoint = canvasPickMode ? elementId === fixedPickId : false;
                      const isPickable = canvasPickMode ? !isFixedEndpoint : false;

                      return isVisible ? (
                        <Element
                          key={element._id}
                          element={element}
                          isSelected={selectedElement?._id === element._id}
                          isPreviewed={isPreviewed}
                          isSource={selectedConnection && selectedConnection.source === element._id}
                          isTarget={selectedConnection && selectedConnection.target === element._id}
                          position={null}
                          isDragging={draggedElement?._id === element._id}
                          onMouseDown={onCanvasElementMouseDown}
                          onCornerMouseDown={onCanvasCornerMouseDown}
                          onHoverChange={canvasPickMode && isPickable ? onCanvasPickHover : undefined}
                          isPickable={isPickable}
                          isFixedEndpoint={isFixedEndpoint}
                          isUnlinked={isUnlinked}
                          scale={displayScale}
                          isMaximized={isMaximized}
                        />
                      ) : null;
                      });
                    })()}

                    {viewMarker && (
                      // the region a ?view= link points at
                      <Box sx={{ position: 'absolute', left: viewMarker.x * displayScale - 4, top: viewMarker.y * displayScale - 4,
                        width: viewMarker.width * displayScale + 8, height: viewMarker.height * displayScale + 8,
                        border: '2px dashed #2dd4bf', boxShadow: '0 0 0 4px rgba(45,212,191,.2)', borderRadius: '4px', pointerEvents: 'none', zIndex: 3 }} />
                    )}

                    {connections.map((connection) => {
                      const sourceId = getElementId(connection.source);
                      const targetId = getElementId(connection.target);
                      const source = sourceId ? elementsById.get(sourceId) || null : null;
                      const target = targetId ? elementsById.get(targetId) || null : null;
                      // A 'labels' edge's target may be a Connection (naming a pipe) instead
                      // of an Element (Phase 1.5) — resolve against connections as a fallback,
                      // and compute a point on the pipe's own path to draw the line to, since
                      // a Connection has no boundingBox center the way an Element does.
                      const targetConnection = !target && targetId ? connectionsById.get(targetId) || null : null;
                      let targetPoint = null;
                      if (targetConnection && source?.boundingBox) {
                        const labelCenter = {
                          x: source.boundingBox.x + source.boundingBox.width / 2,
                          y: source.boundingBox.y + source.boundingBox.height / 2,
                        };
                        let segments = targetConnection.metadata?.path_data?.path;
                        if (!Array.isArray(segments) || segments.length === 0) {
                          const pipeSourceEl = elementsById.get(getElementId(targetConnection.source));
                          const pipeTargetEl = elementsById.get(getElementId(targetConnection.target));
                          if (pipeSourceEl?.boundingBox && pipeTargetEl?.boundingBox) {
                            segments = [{
                              start: [pipeSourceEl.boundingBox.x + pipeSourceEl.boundingBox.width / 2, pipeSourceEl.boundingBox.y + pipeSourceEl.boundingBox.height / 2],
                              end: [pipeTargetEl.boundingBox.x + pipeTargetEl.boundingBox.width / 2, pipeTargetEl.boundingBox.y + pipeTargetEl.boundingBox.height / 2],
                            }];
                          }
                        }
                        if (segments) targetPoint = nearestPointOnPolyline(labelCenter, segments);
                      }
                      const isRelatedToSelectedElement = selectedElement && (sourceId === selectedElement._id || targetId === selectedElement._id);
                      const isUnlinked = Boolean(unlinked?.connections.has(String(connection._id)));
                      const isPreviewed = !selectedElement && !selectedConnection && previewItem?.kind === 'connection' && previewItem.id === String(connection._id);
                      const isVisible = isPreviewed || selectedConnection?._id === connection._id || ((showAllObjects || isUnlinked || selectedConnection?._id === connection._id || isRelatedToSelectedElement) && !(hideRejected && connection.metadata?.rejected) && !(hideTraces && isTraceConnection(connection) && selectedConnection?._id !== connection._id)
                        && (selectedConnection?._id === connection._id || isRelatedToSelectedElement || onScreen(connectionBoxes.get(String(connection._id)))));
                      const isConnectionSelected = selectedConnection?._id === connection._id;
                      return isVisible ? (
                        <Connection
                          key={connection._id}
                          connection={connection}
                          source={source}
                          target={target}
                          targetPoint={targetPoint}
                          isUnlinked={isUnlinked}
                          isHighlighted={isConnectionSelected || isPreviewed || (selectedElement && (selectedElement._id === source?._id || selectedElement._id === target?._id))}
                          scale={displayScale}
                          onClick={onCanvasConnectionClick}
                          onEndpointMouseDown={isConnectionSelected ? onCanvasEndpointMouseDown : undefined}
                          onVertexMouseDown={isConnectionSelected ? onCanvasVertexMouseDown : undefined}
                        />
                      ) : null;
                    })}

                    <ConnectionCreationOverlay
                      isActive={isCreatingConnection}
                      elements={overlayElements}
                      connections={overlayConnections}
                      visibleRect={cullRect}
                      scale={displayScale}
                      containerRef={containerRef}
                      onComplete={handleConnectionCreationComplete}
                      onCancel={handleConnectionCreationCancel}
                      rerouteAnchor={reroutingConnection
                        ? { point: reroutingConnection.anchorPoint, connectionId: reroutingConnection.connectionId, anchoredEnd: reroutingConnection.anchoredEnd }
                        : null}
                      onRerouteComplete={handleRerouteComplete}
                      onSplitConnection={handleSplitConnection}
                      onDescribeConnection={handleDescribeConnection}
                    />

                    {/* Element rectangle draw preview */}
                    {isCreatingElement && (
                      <svg style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', overflow: 'visible' }}>
                        {/* First-corner anchor dot */}
                        {elementDrawStart && (
                          <circle
                            cx={elementDrawStart.x * displayScale}
                            cy={elementDrawStart.y * displayScale}
                            r={5}
                            fill="#2dd4bf"
                            opacity={0.9}
                          />
                        )}
                        {/* Live preview rectangle */}
                        {elementDrawStart && elementMousePos && (
                          <rect
                            x={Math.min(elementDrawStart.x, elementMousePos.x) * displayScale}
                            y={Math.min(elementDrawStart.y, elementMousePos.y) * displayScale}
                            width={Math.abs(elementMousePos.x - elementDrawStart.x) * displayScale}
                            height={Math.abs(elementMousePos.y - elementDrawStart.y) * displayScale}
                            fill="rgba(45,212,191,0.08)"
                            stroke="#2dd4bf"
                            strokeWidth={1.5}
                            strokeDasharray="6 4"
                          />
                        )}
                      </svg>
                    )}

                    {/* Rapid annotate: ghost of the click box under the cursor, or the drag box */}
                    {isRapidMode && (rapidDrag || (rapidTool === 'symbol' && rapidMouse && rapidBoxSize)) && (
                      <svg style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', overflow: 'visible', zIndex: 20 }}>
                        {rapidDrag ? (
                          <rect
                            x={Math.min(rapidDrag.start.x, rapidDrag.current.x) * displayScale}
                            y={Math.min(rapidDrag.start.y, rapidDrag.current.y) * displayScale}
                            width={Math.abs(rapidDrag.current.x - rapidDrag.start.x) * displayScale}
                            height={Math.abs(rapidDrag.current.y - rapidDrag.start.y) * displayScale}
                            fill="rgba(167,139,250,0.08)" stroke={VLM_COLORS.pending} strokeWidth={1.5} strokeDasharray="6 4"
                          />
                        ) : (
                          <>
                            <rect
                              x={(rapidMouse.x - rapidBoxSize.width / 2) * displayScale}
                              y={(rapidMouse.y - rapidBoxSize.height / 2) * displayScale}
                              width={rapidBoxSize.width * displayScale}
                              height={rapidBoxSize.height * displayScale}
                              fill="rgba(167,139,250,0.06)" stroke={VLM_COLORS.pending} strokeWidth={1.25} strokeDasharray="4 3" opacity={0.85}
                            />
                            <circle cx={rapidMouse.x * displayScale} cy={rapidMouse.y * displayScale} r={2} fill={VLM_COLORS.pending} />
                          </>
                        )}
                      </svg>
                    )}

                    {/* Zoom selection box preview */}
                    {isZoomMode && zoomSelectionStart && zoomSelectionCurrent &&
                      (Math.abs(zoomSelectionCurrent.x - zoomSelectionStart.x) * displayScale > 5 ||
                       Math.abs(zoomSelectionCurrent.y - zoomSelectionStart.y) * displayScale > 5) && (
                      <svg style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', overflow: 'visible', zIndex: 20 }}>
                        <rect
                          x={Math.min(zoomSelectionStart.x, zoomSelectionCurrent.x) * displayScale}
                          y={Math.min(zoomSelectionStart.y, zoomSelectionCurrent.y) * displayScale}
                          width={Math.abs(zoomSelectionCurrent.x - zoomSelectionStart.x) * displayScale}
                          height={Math.abs(zoomSelectionCurrent.y - zoomSelectionStart.y) * displayScale}
                          fill="rgba(99,179,237,0.08)"
                          stroke="#63b3ed"
                          strokeWidth={1.5}
                          strokeDasharray="6 3"
                        />
                      </svg>
                    )}

                    {/* Suggested text links (name, descriptions): label → element, drawn until saved */}
                    {linkPreviews.map(preview => {
                      const label = elements.find(e => String(e._id) === preview.labelId);
                      const el = elements.find(e => String(e._id) === preview.elementId);
                      if (!label?.boundingBox || !el?.boundingBox) return null;
                      const colour = preview.accepted ? '#f5a524' : '#a78bfa';
                      const lb = label.boundingBox;
                      const x1 = (lb.x + lb.width / 2) * displayScale;
                      const y1 = (lb.y + lb.height / 2) * displayScale;
                      const x2 = (el.boundingBox.x + el.boundingBox.width / 2) * displayScale;
                      const y2 = (el.boundingBox.y + el.boundingBox.height / 2) * displayScale;
                      return (
                        <svg key={`link-preview-${preview.labelId}`} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', overflow: 'visible', zIndex: 15 }}>
                          <rect x={lb.x * displayScale - 3} y={lb.y * displayScale - 3} width={lb.width * displayScale + 6} height={lb.height * displayScale + 6}
                            fill={`${colour}22`} stroke={colour} strokeWidth={1.5} strokeDasharray="4 3" rx={3} />
                          <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={colour} strokeWidth={2} strokeDasharray="6 4" opacity={0.9} />
                          <circle cx={x2} cy={y2} r={4} fill={colour} />
                        </svg>
                      );
                    })}

                    {/* Ghost line preview in canvas pick mode */}
                    {canvasPickMode && hoverElementId && (() => {
                      const pickConn = connections.find(c => c._id === canvasPickMode.connectionId);
                      const fixedId = canvasPickMode.which === 'source' ? getElId(pickConn?.target) : getElId(pickConn?.source);
                      const fixedEl = elements.find(e => String(e._id) === fixedId);
                      const hoveredEl = elements.find(e => String(e._id) === hoverElementId);
                      if (!fixedEl?.boundingBox || !hoveredEl?.boundingBox) return null;
                      const x1 = (fixedEl.boundingBox.x + fixedEl.boundingBox.width / 2) * displayScale;
                      const y1 = (fixedEl.boundingBox.y + fixedEl.boundingBox.height / 2) * displayScale;
                      const x2 = (hoveredEl.boundingBox.x + hoveredEl.boundingBox.width / 2) * displayScale;
                      const y2 = (hoveredEl.boundingBox.y + hoveredEl.boundingBox.height / 2) * displayScale;
                      return (
                        <svg key="ghost-line" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', overflow: 'visible', zIndex: 10 }}>
                          <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="#2dd4bf" strokeWidth={2} strokeDasharray="7 4" opacity={0.75} />
                          <circle cx={x2} cy={y2} r={5} fill="#2dd4bf" opacity={0.85} />
                        </svg>
                      );
                    })()}
                  </Box>
                )}
              </Box>

              {/* floating coords */}
              <Box sx={{ position: 'absolute', bottom: '14px', left: '14px', fontFamily: MONO, fontSize: '11px', color: '#7f93b8', background: 'rgba(8,15,30,.7)', border: '1px solid rgba(120,150,200,.18)', borderRadius: '7px', px: '10px', py: '6px', pointerEvents: 'none' }}>
                {Math.round(scale * 100)}%
              </Box>

            </Box>

            <Box sx={{ display: viewMode === 'sheet' ? 'none' : 'contents' }}>
            <DrawingSidePane
              ref={sidebarRef}
              onLinkPreview={setLinkPreviews}
              sidebarOpen={sidebarOpen && viewMode !== 'sheet'}
              toggleSidebar={toggleSidebar}
              elements={elements}
              connections={connections}
              hideRejected={hideRejected}
              selectedElement={selectedElement}
              selectedConnection={selectedConnection}
              handleElementSelect={handleElementSelect}
              handleConnectionSelect={handleConnectionSelect}
              previewItem={previewItem}
              onPreviewItem={setPreviewItem}
              handleConnectionClick={handleConnectionClick}
              handleToggleConfirmedElement={handleToggleConfirmedElement}
              handleToggleConfirmedConnection={handleToggleConfirmedConnection}
              setElements={setElements}
              setConnections={setConnections}
              setSelectedElement={setSelectedElement}
              setSelectedConnection={setSelectedConnection}
              notify={notify}
              drawingId={id}
              onEnterCanvasPickMode={handleEnterCanvasPickMode}
              onExitCanvasPickMode={handleExitCanvasPickMode}
              onAddElementForEndpoint={handleAddElementForEndpoint}
              onVlmClassify={isAdmin ? requestAssist : undefined}
              unlinked={unlinked}
            />
            </Box>
          </>
        )}
      </Box>

    </Box>

    <ConnectionEditDialog
        open={editConnectionDialogOpen}
        onClose={handleConnectionDialogClose}
        connection={selectedConnection}
        editData={editConnectionData}
        setEditData={setEditConnectionData}
        onSave={handleConnectionUpdate}
        onDelete={handleDeleteConnection}
        onToggleConfirmed={handleToggleConfirmedConnection}
        elements={elements}
      />

    </>
  );
};

export default DrawingView;
